import "../Overview/EndpointOverview.css";
import "../Overview/EndpointDataSections.css";
import "../CloudAnalytics/CloudEntityAnalytics.css";
import "./VaultService.css";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import {
    Search,
    Key,
    Users,
    Lock,
    Plus,
    MoreVertical,
    Copy,
    Eye,
    EyeOff,
    RefreshCw,
    Trash2,
    Edit,
    CheckCircle2,
    XCircle,
    Clock,
    AlertTriangle,
    Activity,
    History,
    Ban,
    KeyRound,
    Building2,
    AppWindow,
    ScrollText,
    Filter,
    Download,
    Loader2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import OverviewEmptyState from "../Overview/OverviewEmptyState";
import CardPagination from "../CardPagination";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import { useToast } from "../../../hooks/use-toast";
import {
    createVaultApp,
    createVaultClient,
    createVaultSecret,
    listVaultApps,
    listVaultClients,
    listVaultSecrets,
    listVaultTokens,
    deleteVaultClient,
    deleteVaultApp,
    deleteVaultSecret,
    regenerateVaultAppSecret,
    updateVaultAppScopes,
    disableVaultApp,
    enableVaultApp,
    disableVaultClient,
    enableVaultClient,
    updateVaultSecret,
    listVaultAuditLogs,
    getVaultOverview,
    VaultClientResponse,
    revokeVaultTokenById,
    revokeAppAllTokens,
    getVaultSecretValue,
    updateVaultClient,
} from "../../../services/vaultService";

interface VaultClient {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    active: boolean;
    appsCount: number;
    secretsCount: number;
}

interface VaultClientApp {
    id: string;
    clientId: string;
    clientName: string;
    name: string;
    clientSecret: string;
    scopes: ("read" | "write" | "delete")[];
    allowedLabels: string[];
    active: boolean;
    createdAt: string;
    updatedAt: string;
    activeTokens: number;
    appId: string;
}

interface VaultToken {
    id: string;
    appId: string;
    appName: string;
    clientName: string;
    token: string;
    expiresAt: string;
    revoked: boolean;
    createdAt: string;
    lastUsedAt: string | null;
    sourceIp: string;
    isExpired?: boolean;
    status?: string;
}

interface VaultSecret {
    id: string;
    clientId: string;
    clientName: string;
    label: string;
    vaultPath: string;
    version: number;
    createdAt: string;
    updatedAt: string;
    accessCount: number;
    appSecret?: string;
}

interface VaultAuditLog {
    id: string;
    timestamp: string;
    eventType: string;
    actorId: string;
    actorType: "admin" | "app";
    clientId: string;
    clientName: string;
    appId: string | null;
    appName: string | null;
    secretLabel: string | null;
    vaultPath: string | null;
    success: boolean;
    sourceIp: string;
    additionalData: string;
    requestId?: string;
    userAgent?: string;
    metadata?: string;
    principalName?: string;
}


type VaultTone = "primary" | "success" | "warning" | "danger" | "orange" | "violet" | "teal" | "muted";

const VAULT_TABS: { value: string; label: string; icon: LucideIcon }[] = [
    { value: "overview", label: "Overview", icon: Activity },
    { value: "clients", label: "Clients", icon: Building2 },
    { value: "apps", label: "Client Apps", icon: AppWindow },
    { value: "secrets", label: "Secrets", icon: Lock },
    { value: "tokens", label: "Tokens", icon: KeyRound },
    { value: "audit", label: "Audit Logs", icon: ScrollText },
];

const APP_SCOPES = ["read", "write", "delete"];

const SCOPE_TONES: Record<string, VaultTone> = {
    read: "primary",
    write: "warning",
    delete: "danger",
};

const MASKED_SECRET = "••••••••••••••••••••••••••••••••";

function VaultPill({ tone, icon: Icon, children }: { tone: VaultTone; icon?: LucideIcon; children: ReactNode }) {
    return (
        <span className="vault-pill" data-tone={tone}>
            {Icon && <Icon aria-hidden="true" />}
            {children}
        </span>
    );
}

function VaultStatus({ active }: { active: boolean }) {
    return active ? (
        <VaultPill tone="success" icon={CheckCircle2}>Active</VaultPill>
    ) : (
        <VaultPill tone="muted" icon={XCircle}>Inactive</VaultPill>
    );
}

function VaultScopes({ scopes }: { scopes: string[] }) {
    return (
        <div className="vault-chip-list">
            {scopes.map((scope) => (
                <span key={scope} className="vault-scope" data-tone={SCOPE_TONES[scope]}>{scope}</span>
            ))}
        </div>
    );
}

function VaultStatCard({ icon: Icon, label, value, tone }: { icon: LucideIcon; label: string; value: number; tone?: string }) {
    return (
        <Card className="overview-card vault-stat-card" data-tone={tone}>
            <span className="vault-stat-icon" aria-hidden="true"><Icon /></span>
            <div className="vault-stat-copy">
                <span className="vault-stat-value">{value.toLocaleString()}</span>
                <span className="vault-stat-label">{label}</span>
            </div>
        </Card>
    );
}

function VaultSearchField({ value, placeholder, onChange }: { value: string; placeholder: string; onChange: (value: string) => void }) {
    return (
        <div className="endpoint-filter-field endpoint-filter-search">
            <span className="endpoint-filter-label"><Search />Search</span>
            <div className="vault-search">
                <Search aria-hidden="true" />
                <Input
                    placeholder={placeholder}
                    aria-label={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
            </div>
        </div>
    );
}

function VaultFilterField({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: ReactNode }) {
    return (
        <div className="endpoint-filter-field">
            <span className="endpoint-filter-label"><Icon />{label}</span>
            {children}
        </div>
    );
}

function VaultTableState({ colSpan, icon: Icon, loading = false, message }: { colSpan: number; icon: LucideIcon; loading?: boolean; message: string }) {
    return (
        <TableRow className="vault-state-row">
            <TableCell colSpan={colSpan}>
                <div className="vault-state" role={loading ? "status" : undefined}>
                    <span className="vault-state-icon" aria-hidden="true">
                        {loading ? <Loader2 className="animate-spin" /> : <Icon />}
                    </span>
                    <p>{message}</p>
                </div>
            </TableCell>
        </TableRow>
    );
}

function VaultDialogHeader({ icon: Icon, tone = "primary", title, subtitle }: { icon: LucideIcon; tone?: VaultTone; title: string; subtitle: string }) {
    return (
        <DialogHeader className="vault-dialog-header">
            <span className="vault-dialog-icon" data-tone={tone} aria-hidden="true"><Icon /></span>
            <div className="min-w-0">
                <DialogTitle className="vault-dialog-title">{title}</DialogTitle>
                <DialogDescription className="vault-dialog-subtitle">{subtitle}</DialogDescription>
            </div>
        </DialogHeader>
    );
}

function VaultEntity({ icon: Icon, tone, name, mono = false, sub, children }: { icon: LucideIcon; tone?: string; name: string; mono?: boolean; sub?: string; children?: ReactNode }) {
    return (
        <div className="vault-entity">
            <span className="vault-row-icon" data-tone={tone} aria-hidden="true"><Icon /></span>
            <div className="min-w-0">
                <p className="vault-entity-name" data-mono={mono}>{name}</p>
                {sub && <p className="vault-entity-sub">{sub}</p>}
                {children && <div className="vault-entity-meta">{children}</div>}
            </div>
        </div>
    );
}

function VaultValueField({ label, value, children }: { label: string; value: ReactNode; children?: ReactNode }) {
    return (
        <div className="vault-field">
            <span className="vault-field-label">{label}</span>
            <div className="vault-value-row">
                <code className="vault-value">{value}</code>
                {children}
            </div>
        </div>
    );
}

export default function VaultServiceManagement() {
    const [activeTab, setActiveTab] = useState("overview");
    const [searchTerm, setSearchTerm] = useState("");
    const { toast } = useToast();

    const [clients, setClients] = useState<VaultClient[]>([]);
    const [clientApps, setClientApps] = useState<VaultClientApp[]>([]);
    const [tokens, setTokens] = useState<VaultToken[]>([]);
    const [secrets, setSecrets] = useState<VaultSecret[]>([]);
    const [auditLogs, setAuditLogs] = useState<VaultAuditLog[]>([]);

    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [isLoadingApps, setIsLoadingApps] = useState(false);
    const [isLoadingSecrets, setIsLoadingSecrets] = useState(false);
    const [isLoadingTokens, setIsLoadingTokens] = useState(false);
    const [isLoadingAuditLogs, setIsLoadingAuditLogs] = useState(false);
    const [isSavingClient, setIsSavingClient] = useState(false);
    const [isSavingApp, setIsSavingApp] = useState(false);
    const [isSavingSecret, setIsSavingSecret] = useState(false);

    const mapClientFromApi = (client: VaultClientResponse): VaultClient => ({
        id: client.client_id || client.client_name,
        name: client.client_name,
        description: client.description || "",
        createdAt: client.created_at,
        updatedAt: client.updated_at || client.created_at,
        active: client.enabled,
        appsCount: client.apps_count || 0,
        secretsCount: client.secrets_count || 0,
    });

    const apiToUiScopes = (scopes: string[]) =>
        scopes
            .map((scope) => scope.replace("secret:", "") as "read" | "write" | "delete")
            .filter((scope) => scope === "read" || scope === "write" || scope === "delete");

    const uiToApiScopes = (scopes: string[]) =>
        scopes.map((scope) => (scope.startsWith("secret:") ? scope : `secret:${scope}`));

    const generateLocalId = () =>
        (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `local-${Date.now()}`);

    useEffect(() => {
        const fetchClients = async () => {
            setIsLoadingClients(true);
            try {
                const response = await listVaultClients();
                if (response?.clients) {
                    setClients(response.clients.map(mapClientFromApi));
                } else {
                    setClients([]);
                }
            } catch (error: any) {
                console.error("Failed to load vault clients", error);
                setClients([]);
                toast({
                    title: "Unable to load clients",
                    description: typeof error === "string" ? error : "Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoadingClients(false);
            }
        };

        const fetchApps = async () => {
            try {
                const response = await listVaultApps();
                if (response?.applications) {
                    const mappedApps = response.applications.map((app) => {
                        const matchingClient = clients.find(c => c.name === app.client_name);
                        return {
                            id: generateLocalId(),
                            clientId: matchingClient?.id || app.client_name,
                            clientName: app.client_name,
                            name: app.app_name,
                            clientSecret: app.app_secret ? app.app_secret : "••••••••••••••••",
                            scopes: apiToUiScopes(app.scopes || []),
                            allowedLabels: app.allowed_labels || [],
                            active: app.enabled,
                            createdAt: app.created_at,
                            updatedAt: app.updated_at || app.created_at,
                            activeTokens: (app as any).active_tokens || 0,
                            appId: (app as any).app_id || app.app_name,
                        };
                    });
                    setClientApps(mappedApps);
                } else {
                    setClientApps([]);
                }
            } catch (error: any) {
                console.error("Failed to load vault apps on mount", error);
                setClientApps([]);
            }
        };

        const fetchSecrets = async () => {
            try {
                const response = await listVaultSecrets();
                if (response?.secrets) {
                    const mappedSecrets = response.secrets.map((secret: any) => {
                        const matchingClient = clients.find(c => c.name === secret.clientName);
                        return {
                            id: generateLocalId(),
                            clientId: matchingClient?.id || secret.clientName,
                            clientName: secret.clientName,
                            label: secret.label,
                            vaultPath: secret.vaultPath,
                            version: secret.version,
                            createdAt: secret.lastUpdated,
                            updatedAt: secret.lastUpdated,
                            accessCount: secret.accessCount,
                            appSecret: secret.app_secret ? secret.app_secret : "••••••••••••••••",
                        };
                    });
                    setSecrets(mappedSecrets);
                } else {
                    setSecrets([]);
                }
            } catch (error: any) {
                console.error("Failed to load vault secrets on mount", error);
                setSecrets([]);
            }
        };

        const fetchAuditLogs = async () => {
            setIsLoadingAuditLogs(true);
            try {
                const response = await listVaultAuditLogs({ page: 0, size: 50 });
                if (response?.items) {
                    const mappedLogs = response.items.map((log: any, idx: number) => ({
                        id: log.id?.toString() || `audit-${idx}`,
                        timestamp: log.timestamp,
                        eventType: log.action?.toLowerCase().replace(/([A-Z])/g, "_$1") || "unknown",
                        actorId: "system",
                        actorType: "admin" as const,
                        clientId: log.clientId || log.clientName,
                        clientName: log.clientName,
                        appId: log.appName ? log.appName : null,
                        appName: log.appName || null,
                        secretLabel: log.resourcePath || null,
                        vaultPath: log.resourcePath || null,
                        success: log.success,
                        sourceIp: log.ipAddress || "—",
                        additionalData: log.errorMessage || "{}",
                        requestId: log.requestId,
                        userAgent: log.userAgent,
                        metadata: log.metadata,
                        principalName: log.principalName
                    }));
                    setAuditLogs(mappedLogs);
                } else {
                    setAuditLogs([]);
                }
            } catch (error: any) {
                console.error("Failed to load audit logs on mount", error);
                setAuditLogs([]);
            } finally {
                setIsLoadingAuditLogs(false);
            }
        };

        fetchClients();
        fetchApps();
        fetchSecrets();
        fetchAuditLogs();
    }, [toast]);

    useEffect(() => {
        const loadTabData = async () => {
            if (activeTab === "clients") {
                setIsLoadingClients(true);
                try {
                    const response = await listVaultClients();
                    if (response?.clients) {
                        setClients(response.clients.map(mapClientFromApi));
                    } else {
                        setClients([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault clients", error);
                    setClients([]);
                    toast({
                        title: "Unable to load clients",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingClients(false);
                }
            } else if (activeTab === "apps") {
                setIsLoadingApps(true);
                try {
                    const response = await listVaultApps();
                    console.log("Apps API Response:", response);
                    if (response?.applications) {
                        const mappedApps = response.applications.map((app) => {
                            const matchingClient = clients.find(c => c.name === app.client_name);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || app.client_name,
                                clientName: app.client_name,
                                name: app.app_name,
                                clientSecret: app.app_secret ? app.app_secret : "••••••••••••••••",
                                scopes: apiToUiScopes(app.scopes || []),
                                allowedLabels: app.allowed_labels || [],
                                active: app.enabled,
                                createdAt: app.created_at,
                                updatedAt: app.updated_at || app.created_at,
                                activeTokens: (app as any).active_tokens || 0,
                                appId: (app as any).app_id || app.app_name,
                            };
                        });
                        setClientApps(mappedApps);
                    } else {
                        setClientApps([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault apps", error);
                    setClientApps([]);
                    toast({
                        title: "Unable to load apps",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingApps(false);
                }
            } else if (activeTab === "secrets") {
                setIsLoadingSecrets(true);
                try {
                    const response = await listVaultSecrets();
                    console.log("Secrets API Response:", response);
                    if (response?.secrets) {
                        const mappedSecrets = response.secrets.map((secret: any) => {
                            const matchingClient = clients.find(c => c.name === secret.clientName);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || secret.clientName,
                                clientName: secret.clientName,
                                label: secret.label,
                                vaultPath: secret.vaultPath,
                                version: secret.version,
                                createdAt: secret.lastUpdated,
                                updatedAt: secret.lastUpdated,
                                accessCount: secret.accessCount,
                                appSecret: secret.app_secret ? secret.app_secret : "••••••••••••••••",
                            };
                        });
                        setSecrets(mappedSecrets);
                    } else {
                        setSecrets([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault secrets", error);
                    setSecrets([]);
                    toast({
                        title: "Unable to load secrets",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingSecrets(false);
                }
            } else if (activeTab === "tokens") {
                setIsLoadingTokens(true);
                try {
                    const response = await listVaultTokens({ page: 0, size: 100 });
                    console.log("Tokens API Response:", response);
                    if (response?.tokens && response.tokens.length > 0) {
                        const mappedTokens = response.tokens.map((token: any) => ({
                            id: token.id?.toString() || `token-${Date.now()}`,
                            appId: token.app,
                            appName: token.app,
                            clientName: token.client,
                            token: token.token,
                            expiresAt: token.expiresAt,
                            revoked: token.isRevoked,
                            createdAt: token.created,
                            lastUsedAt: token.lastUsed === "Never" ? null : token.lastUsed,
                            sourceIp: token.sourceIp || "Not used",
                            isExpired: token.isExpired,
                            status: token.status,
                        }));
                        setTokens(mappedTokens);
                    } else {
                        setTokens([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load vault tokens", error);
                    setTokens([]);
                    toast({
                        title: "Unable to load tokens",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingTokens(false);
                }
            } else if (activeTab === "audit") {
                setIsLoadingAuditLogs(true);
                try {
                    const response = await listVaultAuditLogs({ page: 0, size: 50 });
                    if (response?.items) {
                        const mappedLogs = response.items.map((log: any, idx: number) => ({
                            id: log.id?.toString() || `audit-${idx}`,
                            timestamp: log.timestamp,
                            eventType: log.action?.toLowerCase().replace(/([A-Z])/g, "_$1") || "unknown",
                            actorId: "system",
                            actorType: "admin" as const,
                            clientId: log.clientId || log.clientName,
                            clientName: log.clientName,
                            appId: log.appName ? log.appName : null,
                            appName: log.appName || null,
                            secretLabel: log.resourcePath || null,
                            vaultPath: log.resourcePath || null,
                            success: log.success,
                            sourceIp: log.ipAddress || "—",
                            additionalData: log.errorMessage || "{}",
                            requestId: log.requestId,
                            userAgent: log.userAgent,
                            metadata: log.metadata,
                            principalName: log.principalName
                        }));
                        setAuditLogs(mappedLogs);
                    } else {
                        setAuditLogs([]);
                    }
                } catch (error: any) {
                    console.error("Failed to load audit logs", error);
                } finally {
                    setIsLoadingAuditLogs(false);
                }
            } else if (activeTab === "overview") {
                try {
                    const response = await getVaultOverview();
                    console.log("Overview API Response:", response);
                } catch (error: any) {
                    console.error("Failed to load overview data", error);
                }
            }
        };

        loadTabData();
    }, [activeTab]);

    const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);
    const [isCreateAppOpen, setIsCreateAppOpen] = useState(false);
    const [isCreateSecretOpen, setIsCreateSecretOpen] = useState(false);
    const [isCredentialsDialogOpen, setIsCredentialsDialogOpen] = useState(false);
    const [generatedCredentials, setGeneratedCredentials] = useState<{ clientId: string; clientSecret: string; clientName: string; appId?: string } | null>(null);
    const [showSecret, setShowSecret] = useState<string | null>(null);
    const [showGeneratedSecret, setShowGeneratedSecret] = useState(false);
    const [isViewCredentialsOpen, setIsViewCredentialsOpen] = useState(false);
    const [viewingApp, setViewingApp] = useState<VaultClientApp | null>(null);
    const [showViewingSecret, setShowViewingSecret] = useState(false);

    const [isEditClientOpen, setIsEditClientOpen] = useState(false);
    const [editingClient, setEditingClient] = useState<VaultClient | null>(null);
    const [editClientName, setEditClientName] = useState("");
    const [editClientDescription, setEditClientDescription] = useState("");
    const [isDeleteClientOpen, setIsDeleteClientOpen] = useState(false);
    const [deletingClient, setDeletingClient] = useState<VaultClient | null>(null);

    const [isEditAppOpen, setIsEditAppOpen] = useState(false);
    const [editingApp, setEditingApp] = useState<VaultClientApp | null>(null);
    const [editAppName, setEditAppName] = useState("");
    const [editAppLabels, setEditAppLabels] = useState("");
    const [editAppScopes, setEditAppScopes] = useState<string[]>([]);
    const [isDeleteAppOpen, setIsDeleteAppOpen] = useState(false);
    const [deletingApp, setDeletingApp] = useState<VaultClientApp | null>(null);
    const [isRegenerateSecretOpen, setIsRegenerateSecretOpen] = useState(false);
    const [regeneratingApp, setRegeneratingApp] = useState<VaultClientApp | null>(null);
    const [newlyGeneratedSecret, setNewlyGeneratedSecret] = useState("");
    const [showNewlyGeneratedSecret, setShowNewlyGeneratedSecret] = useState(false);

    const [isViewSecretOpen, setIsViewSecretOpen] = useState(false);
    const [viewingSecret, setViewingSecret] = useState<VaultSecret | null>(null);
    const [secretValue, setSecretValue] = useState("");
    const [showSecretValue, setShowSecretValue] = useState(false);
    const [isUpdateSecretOpen, setIsUpdateSecretOpen] = useState(false);
    const [updatingSecret, setUpdatingSecret] = useState<VaultSecret | null>(null);
    const [updateSecretValue, setUpdateSecretValue] = useState("");
    const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
    const [versionHistorySecret, setVersionHistorySecret] = useState<VaultSecret | null>(null);
    const [isDeleteSecretOpen, setIsDeleteSecretOpen] = useState(false);
    const [deletingSecret, setDeletingSecret] = useState<VaultSecret | null>(null);

    const [isRevokeTokenOpen, setIsRevokeTokenOpen] = useState(false);
    const [revokingToken, setRevokingToken] = useState<VaultToken | null>(null);

    const [isDeactivateClientOpen, setIsDeactivateClientOpen] = useState(false);
    const [deactivatingClient, setDeactivatingClient] = useState<VaultClient | null>(null);
    const [isDeactivateAppOpen, setIsDeactivateAppOpen] = useState(false);
    const [deactivatingApp, setDeactivatingApp] = useState<VaultClientApp | null>(null);
    const [isRevokeAllTokensOpen, setIsRevokeAllTokensOpen] = useState(false);
    const [revokeAllTokensApp, setRevokeAllTokensApp] = useState<VaultClientApp | null>(null);

    const [newClientName, setNewClientName] = useState("");
    const [newClientDescription, setNewClientDescription] = useState("");
    const [newAppName, setNewAppName] = useState("");
    const [newAppClientId, setNewAppClientId] = useState("");
    const [newAppScopes, setNewAppScopes] = useState<string[]>(["read"]);
    const [newAppLabels, setNewAppLabels] = useState("");
    const [newSecretLabel, setNewSecretLabel] = useState("");
    const [newSecretValue, setNewSecretValue] = useState("");
    const [newSecretClientId, setNewSecretClientId] = useState("");

    const [auditEventFilter, setAuditEventFilter] = useState("all");
    const [auditClientFilter, setAuditClientFilter] = useState("all");
    const [clientsFilter, setClientsFilter] = useState("all");
    const [appsFilter, setAppsFilter] = useState("all");
    const [secretsFilter, setSecretsFilter] = useState("all");
    const [tokensFilter, setTokensFilter] = useState("all");

    const ITEMS_PER_PAGE = 10;
    const [clientsPage, setClientsPage] = useState(1);
    const [appsPage, setAppsPage] = useState(1);
    const [secretsPage, setSecretsPage] = useState(1);
    const [tokensPage, setTokensPage] = useState(1);
    const [auditPage, setAuditPage] = useState(1);

    const stats = useMemo(() => ({
        totalClients: clients.length,
        activeClients: clients.filter(c => c.active).length,
        totalApps: clientApps.length,
        activeApps: clientApps.filter(a => a.active).length,
        totalSecrets: secrets.length,
        totalTokens: tokens.length,
        activeTokens: tokens.filter(t =>
            !t.revoked && ((t.isExpired === false) || (t.isExpired === undefined && new Date(t.expiresAt) > new Date()))
        ).length,
        revokedTokens: tokens.filter(t => t.revoked).length,
        recentFailures: auditLogs.filter(l => !l.success).length,
    }), [clients, clientApps, secrets, tokens, auditLogs]);

    const handleRefreshTab = async () => {
        const loadTabData = async () => {
            if (activeTab === "clients") {
                setIsLoadingClients(true);
                try {
                    const response = await listVaultClients();
                    if (response?.clients) {
                        setClients(response.clients.map(mapClientFromApi));
                    } else {
                        setClients([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Clients data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault clients", error);
                    toast({
                        title: "Unable to refresh clients",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingClients(false);
                }
            } else if (activeTab === "apps") {
                setIsLoadingApps(true);
                try {
                    const response = await listVaultApps();
                    if (response?.applications) {
                        const mappedApps = response.applications.map((app) => {
                            const matchingClient = clients.find(c => c.name === app.client_name);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || app.client_name,
                                clientName: app.client_name,
                                name: app.app_name,
                                clientSecret: app.app_secret ? app.app_secret : "••••••••••••••••",
                                scopes: apiToUiScopes(app.scopes || []),
                                allowedLabels: app.allowed_labels || [],
                                active: app.enabled,
                                createdAt: app.created_at,
                                updatedAt: app.updated_at || app.created_at,
                                activeTokens: (app as any).active_tokens || 0,
                                appId: (app as any).app_id || app.app_name,
                            };
                        });
                        setClientApps(mappedApps);
                    } else {
                        setClientApps([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Apps data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault apps", error);
                    toast({
                        title: "Unable to refresh apps",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingApps(false);
                }
            } else if (activeTab === "secrets") {
                setIsLoadingSecrets(true);
                try {
                    const response = await listVaultSecrets();
                    if (response?.secrets) {
                        const mappedSecrets = response.secrets.map((secret: any) => {
                            const matchingClient = clients.find(c => c.name === secret.clientName);
                            return {
                                id: generateLocalId(),
                                clientId: matchingClient?.id || secret.clientName,
                                clientName: secret.clientName,
                                label: secret.label,
                                vaultPath: secret.vaultPath,
                                version: secret.version,
                                createdAt: secret.lastUpdated,
                                updatedAt: secret.lastUpdated,
                                accessCount: secret.accessCount,
                                appSecret: secret.app_secret ? secret.app_secret : "••••••••••••••••",
                            };
                        });
                        setSecrets(mappedSecrets);
                    } else {
                        setSecrets([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Secrets data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault secrets", error);
                    toast({
                        title: "Unable to refresh secrets",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingSecrets(false);
                }
            } else if (activeTab === "tokens") {
                setIsLoadingTokens(true);
                try {
                    const response = await listVaultTokens({ page: 0, size: 100 });
                    if (response?.tokens && response.tokens.length > 0) {
                        const mappedTokens = response.tokens.map((token: any) => ({
                            id: token.id?.toString() || `token-${Date.now()}`,
                            appId: token.app,
                            appName: token.app,
                            clientName: token.client,
                            token: token.token,
                            expiresAt: token.expiresAt,
                            revoked: token.isRevoked,
                            createdAt: token.created,
                            lastUsedAt: token.lastUsed === "Never" ? null : token.lastUsed,
                            sourceIp: token.sourceIp || "Not used",
                            isExpired: token.isExpired,
                            status: token.status,
                        }));
                        setTokens(mappedTokens);
                    } else {
                        setTokens([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Tokens data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load vault tokens", error);
                    toast({
                        title: "Unable to refresh tokens",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingTokens(false);
                }
            } else if (activeTab === "audit") {
                setIsLoadingAuditLogs(true);
                try {
                    const response = await listVaultAuditLogs({ page: 0, size: 50 });
                    if (response?.items) {
                        const mappedLogs = response.items.map((log: any, idx: number) => ({
                            id: log.id?.toString() || `audit-${idx}`,
                            timestamp: log.timestamp,
                            eventType: log.action?.toLowerCase().replace(/([A-Z])/g, "_$1") || "unknown",
                            actorId: "system",
                            actorType: "admin" as const,
                            clientId: log.clientId || log.clientName,
                            clientName: log.clientName,
                            appId: log.appName ? log.appName : null,
                            appName: log.appName || null,
                            secretLabel: log.resourcePath || null,
                            vaultPath: log.resourcePath || null,
                            success: log.success,
                            sourceIp: log.ipAddress || "—",
                            additionalData: log.errorMessage || "{}",
                            requestId: log.requestId,
                            userAgent: log.userAgent,
                            metadata: log.metadata,
                            principalName: log.principalName
                        }));
                        setAuditLogs(mappedLogs);
                    } else {
                        setAuditLogs([]);
                    }
                    toast({
                        title: "Refreshed",
                        description: "Audit logs have been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load audit logs", error);
                    toast({
                        title: "Unable to refresh logs",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                } finally {
                    setIsLoadingAuditLogs(false);
                }
            } else if (activeTab === "overview") {
                try {
                    const overviewResponse = await getVaultOverview();
                    console.log("Overview API Response:", overviewResponse);
                    toast({
                        title: "Refreshed",
                        description: "Overview data has been refreshed.",
                    });
                } catch (error: any) {
                    console.error("Failed to load overview data", error);
                    toast({
                        title: "Unable to refresh overview",
                        description: typeof error === "string" ? error : "Please try again.",
                        variant: "destructive",
                    });
                }
            }
        };
        await loadTabData();
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: "Copied",
            description: `${label} copied to clipboard.`
        });
    };


    const handleCreateClient = async () => {
        if (!newClientName.trim()) {
            toast({
                title: "Client name required",
                description: "Enter a name before creating a client.",
                variant: "destructive",
            });
            return;
        }

        setIsSavingClient(true);
        try {
            const response = await createVaultClient({
                name: newClientName.trim(),
                description: newClientDescription.trim() || undefined,
            });

            const createdClient = mapClientFromApi(response);
            setClients((prev) => [createdClient, ...prev]);

            toast({
                title: "Client created",
                description: `Client "${createdClient.name}" has been created. Now create apps under this client.`,
            });
            setIsCreateClientOpen(false);
            setNewClientName("");
            setNewClientDescription("");
        } catch (error: any) {
            console.error("Failed to create vault client", error);
            toast({
                title: "Client creation failed",
                description: typeof error === "string" ? error : "Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSavingClient(false);
        }
    };

    const handleCreateApp = async () => {
        if (!newAppClientId || !newAppName.trim()) {
            toast({
                title: "Missing details",
                description: "Select a client and provide an app name.",
                variant: "destructive",
            });
            return;
        }

        const selectedClient = clients.find((c) => c.id === newAppClientId);
        const allowedLabels = newAppLabels
            .split(",")
            .map((label) => label.trim())
            .filter(Boolean);

        setIsSavingApp(true);
        try {
            const response = await createVaultApp(selectedClient?.name || newAppClientId, {
                appName: newAppName.trim(),
                description: selectedClient?.description,
                scopes: uiToApiScopes(newAppScopes),
                allowedLabels,
            });

            const app: VaultClientApp = {
                id: generateLocalId(),
                clientId: selectedClient?.id || newAppClientId,
                clientName: response.client_name || selectedClient?.name || newAppClientId,
                name: response.app_name,
                clientSecret: response.app_secret ? response.app_secret : "••••••••••••••••",
                scopes: apiToUiScopes(response.scopes),
                allowedLabels: response.allowed_labels,
                active: response.enabled,
                createdAt: response.created_at,
                updatedAt: response.created_at,
                activeTokens: 0,
                appId: response.app_id || response.app_name,
            };

            setClientApps((prev) => [app, ...prev]);
            setClients((prev) => prev.map((client) => client.id === app.clientId ? { ...client, appsCount: client.appsCount + 1 } : client));
            setGeneratedCredentials({
                clientId: app.clientId,
                clientSecret: app.clientSecret,
                clientName: `${app.clientName} / ${app.name}`,
                appId: app.appId,
            });

            toast({
                title: "App created",
                description: `App "${app.name}" registered under ${app.clientName}. Save the secret now – it is shown only once.`,
            });

            setIsCreateAppOpen(false);
            setIsCredentialsDialogOpen(true);
            setShowGeneratedSecret(false);
            setNewAppName("");
            setNewAppClientId("");
            setNewAppScopes(["read"]);
            setNewAppLabels("");
        } catch (error: any) {
            console.error("Failed to create vault app", error);
            toast({
                title: "App creation failed",
                description: typeof error === "string" ? error : "Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSavingApp(false);
        }
    };

    const handleCreateSecret = async () => {
        if (!newSecretClientId || !newSecretLabel.trim() || !newSecretValue.trim()) {
            toast({
                title: "Missing details",
                description: "Select a client, add a label, and provide a secret value.",
                variant: "destructive",
            });
            return;
        }

        const parsedValue = (() => {
            try {
                return JSON.parse(newSecretValue);
            } catch {
                return { value: newSecretValue };
            }
        })();

        setIsSavingSecret(true);
        try {
            const response = await createVaultSecret({
                clientName: newSecretClientId,
                label: newSecretLabel.trim(),
                description: undefined,
                data: parsedValue,
            });

            const secret: VaultSecret = {
                id: generateLocalId(),
                clientId: newSecretClientId,
                clientName: response.clientName,
                label: response.label,
                vaultPath: response.vaultPath,
                version: response.version,
                createdAt: response.lastUpdated,
                updatedAt: response.lastUpdated,
                accessCount: response.accessCount,
            };

            setSecrets((prev) => [secret, ...prev]);
            setClients((prev) => prev.map((client) => client.id === secret.clientId ? { ...client, secretsCount: client.secretsCount + 1 } : client));
            toast({
                title: "Secret stored",
                description: `Secret "${secret.label}" has been stored for ${secret.clientName}.`,
            });
            setIsCreateSecretOpen(false);
            setNewSecretLabel("");
            setNewSecretValue("");
            setNewSecretClientId("");
        } catch (error: any) {
            console.error("Failed to create vault secret", error);
            toast({
                title: "Secret creation failed",
                description: typeof error === "string" ? error : "Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSavingSecret(false);
        }
    };


    const handleRevokeAllExpired = () => {
        const expiredCount = tokens.filter(t =>
            !t.revoked && (t.isExpired === true || (t.isExpired === undefined && new Date(t.expiresAt) < new Date()))
        ).length;
        toast({
            title: "Expired Tokens Revoked",
            description: `${expiredCount} expired token(s) have been revoked.`
        });
    };

    const openEditClient = (client: VaultClient) => {
        setEditingClient(client);
        setEditClientName(client.name);
        setEditClientDescription(client.description);
        setIsEditClientOpen(true);
    };

    const handleEditClient = async () => {
        if (editingClient && editClientName.trim()) {
            try {
                await updateVaultClient(editingClient.name, {
                    name: editClientName,
                    description: editClientDescription,
                });

                setClients((prev) => prev.map((client) =>
                    client.id === editingClient.id
                        ? {
                            ...client,
                            name: editClientName,
                            description: editClientDescription,
                            updatedAt: new Date().toISOString(),
                        }
                        : client
                ));

                toast({
                    title: "Client Updated",
                    description: `Client "${editClientName}" has been updated successfully.`
                });
                setIsEditClientOpen(false);
                setEditingClient(null);
            } catch (error: any) {
                console.error("Failed to update client", error);
                toast({
                    title: "Update failed",
                    description: error?.message || "Failed to update client",
                    variant: "destructive"
                });
            }
        }
    };

    const openDeleteClient = (client: VaultClient) => {
        setDeletingClient(client);
        setIsDeleteClientOpen(true);
    };

    const handleDeleteClient = async () => {
        if (deletingClient) {
            try {
                await deleteVaultClient(deletingClient.name);
                setClients((prev) => prev.filter((c) => c.id !== deletingClient.id));
                const appsCount = clientApps.filter(a => a.clientId === deletingClient.id).length;
                toast({
                    title: "Client Deleted",
                    description: `Client "${deletingClient.name}" and its ${appsCount} app(s) have been permanently deleted.`,
                    variant: "destructive"
                });
                setIsDeleteClientOpen(false);
                setDeletingClient(null);
            } catch (error: any) {
                console.error("Failed to delete client", error);
                toast({
                    title: "Delete failed",
                    description: error?.message || "Failed to delete client",
                    variant: "destructive"
                });
            }
        }
    };

    const openDeactivateClient = (client: VaultClient) => {
        setDeactivatingClient(client);
        setIsDeactivateClientOpen(true);
    };

    const handleConfirmDeactivateClient = async () => {
        if (deactivatingClient) {
            try {
                if (deactivatingClient.active) {
                    await disableVaultClient(deactivatingClient.name);
                } else {
                    await enableVaultClient(deactivatingClient.name);
                }

                setClients((prev) => prev.map((client) =>
                    client.id === deactivatingClient.id ? { ...client, active: !client.active } : client
                ));

                const appsCount = clientApps.filter(a => a.clientId === deactivatingClient.id && a.active).length;
                const tokensCount = tokens.filter(t =>
                    clientApps.find(a => a.id === t.appId)?.clientId === deactivatingClient.id && !t.revoked
                ).length;
                toast({
                    title: deactivatingClient.active ? "Client Deactivated" : "Client Activated",
                    description: deactivatingClient.active
                        ? `Client "${deactivatingClient.name}" and ${appsCount} app(s) have been deactivated. ${tokensCount} token(s) revoked.`
                        : `Client "${deactivatingClient.name}" has been activated.`
                });
                setIsDeactivateClientOpen(false);
                setDeactivatingClient(null);
            } catch (error: any) {
                console.error("Failed to toggle client status", error);
                toast({
                    title: "Operation failed",
                    description: error?.message || "Failed to toggle client status",
                    variant: "destructive"
                });
            }
        }
    };

    // Client App Handlers
    const openEditApp = (app: VaultClientApp) => {
        setEditingApp(app);
        setEditAppName(app.name);
        setEditAppLabels(app.allowedLabels.join(", "));
        setEditAppScopes([...app.scopes]);
        setIsEditAppOpen(true);
    };

    const handleEditApp = async () => {
        if (editingApp && editAppName.trim()) {
            try {
                const parsedLabels = editAppLabels.split(",").map(s => s.trim()).filter(s => s.length > 0);

                await updateVaultAppScopes(editingApp.name, {
                    name: editAppName,
                    scopes: editAppScopes.map(s => s.includes(":") ? s : `secret:${s}`),
                    allowedLabels: parsedLabels
                });

                const mappedScopes = editAppScopes.filter((s) => s === "read" || s === "write" || s === "delete") as ("read" | "write" | "delete")[];

                setClientApps((prev) => prev.map((app) =>
                    app.id === editingApp.id
                        ? {
                            ...app,
                            name: editAppName,
                            scopes: mappedScopes,
                            allowedLabels: parsedLabels
                        }
                        : app
                ));
                toast({
                    title: "App Updated",
                    description: `App "${editAppName}" has been updated successfully.`
                });
                setIsEditAppOpen(false);
                setEditingApp(null);
            } catch (error: any) {
                console.error("Failed to update app", error);
                toast({
                    title: "Update failed",
                    description: error?.message || "Failed to update app",
                    variant: "destructive"
                });
            }
        }
    };

    const openDeleteApp = (app: VaultClientApp) => {
        setDeletingApp(app);
        setIsDeleteAppOpen(true);
    };

    const handleDeleteApp = async () => {
        if (deletingApp) {
            try {
                await deleteVaultApp(deletingApp.name);
                setClientApps((prev) => prev.filter((a) => a.id !== deletingApp.id));
                toast({
                    title: "App Deleted",
                    description: `App "${deletingApp.name}" and all its tokens have been permanently deleted.`,
                    variant: "destructive"
                });
                setIsDeleteAppOpen(false);
                setDeletingApp(null);
            } catch (error: any) {
                console.error("Failed to delete app", error);
                toast({
                    title: "Delete failed",
                    description: error?.message || "Failed to delete app",
                    variant: "destructive"
                });
            }
        }
    };

    const openRegenerateSecret = (app: VaultClientApp) => {
        setRegeneratingApp(app);
        setIsRegenerateSecretOpen(true);
        setNewlyGeneratedSecret("");
        setShowNewlyGeneratedSecret(false);
    };

    const handleConfirmRegenerateSecret = async () => {
        if (regeneratingApp) {
            try {
                const response = await regenerateVaultAppSecret(regeneratingApp.name);
                const secret = response.app_secret ? response.app_secret : "••••••••••••••••";
                setNewlyGeneratedSecret(secret);
                toast({
                    title: "Secret Regenerated",
                    description: `New client secret generated for "${regeneratingApp.name}". ${response.warning || ""}`
                });
            } catch (error: any) {
                console.error("Failed to regenerate secret", error);
                toast({
                    title: "Regeneration failed",
                    description: error?.message || "Failed to regenerate secret",
                    variant: "destructive"
                });
            }
        }
    };



    const openDeactivateApp = (app: VaultClientApp) => {
        setDeactivatingApp(app);
        setIsDeactivateAppOpen(true);
    };

    const handleConfirmDeactivateApp = async () => {
        if (deactivatingApp) {
            try {
                if (deactivatingApp.active) {
                    await disableVaultApp(deactivatingApp.name);
                } else {
                    await enableVaultApp(deactivatingApp.name);
                }

                setClientApps((prev) => prev.map((app) =>
                    app.id === deactivatingApp.id ? { ...app, active: !app.active } : app
                ));

                const tokensCount = tokens.filter((t) => t.appId === deactivatingApp.id && !t.revoked).length;
                toast({
                    title: deactivatingApp.active ? "App Deactivated" : "App Activated",
                    description: deactivatingApp.active
                        ? `App "${deactivatingApp.name}" has been deactivated. ${tokensCount} active token(s) have been revoked.`
                        : `App "${deactivatingApp.name}" has been activated.`
                });
                setIsDeactivateAppOpen(false);
                setDeactivatingApp(null);
            } catch (error: any) {
                console.error("Failed to toggle app status", error);
                toast({
                    title: "Operation failed",
                    description: error?.message || "Failed to toggle app status",
                    variant: "destructive"
                });
            }
        }
    };

    const openRevokeAllTokens = (app: VaultClientApp) => {
        setRevokeAllTokensApp(app);
        setIsRevokeAllTokensOpen(true);
    };

    const handleConfirmRevokeAllTokens = async () => {
        if (revokeAllTokensApp) {
            try {
                const response = await revokeAppAllTokens(revokeAllTokensApp.clientName, revokeAllTokensApp.name);

                setTokens((prev) => prev.map((t) =>
                    t.appId === revokeAllTokensApp.id ? { ...t, revoked: true } : t
                ));

                setClientApps((prev) => prev.map((a) =>
                    a.id === revokeAllTokensApp.id ? { ...a, activeTokens: 0 } : a
                ));

                toast({
                    title: "All Tokens Revoked",
                    description: `${response.tokensRevoked} token(s) for "${revokeAllTokensApp.name}" have been revoked.`
                });
                setIsRevokeAllTokensOpen(false);
                setRevokeAllTokensApp(null);
            } catch (error: any) {
                console.error("Failed to revoke all tokens", error);
                toast({
                    title: "Revoke failed",
                    description: error?.message || "Failed to revoke tokens",
                    variant: "destructive"
                });
            }
        }
    };

    const openViewSecret = async (secret: VaultSecret) => {
        setViewingSecret(secret);
        setShowSecretValue(false);

        try {
            const response = await getVaultSecretValue(secret.clientName, secret.vaultPath);
            const secretData = typeof response === 'string' ? response : JSON.stringify(response, null, 2);
            setSecretValue(secretData);
        } catch (error: any) {
            console.error("Failed to fetch secret value", error);
            toast({
                title: "Failed to fetch secret",
                description: typeof error === "string" ? error : "Unable to retrieve secret value.",
                variant: "destructive",
            });
            setSecretValue("");
        }

        setIsViewSecretOpen(true);
    };

    const openUpdateSecret = (secret: VaultSecret) => {
        setUpdatingSecret(secret);
        setUpdateSecretValue("");
        setIsUpdateSecretOpen(true);
    };

    const handleUpdateSecret = async () => {
        if (updatingSecret && updateSecretValue.trim()) {
            try {
                let secretData: Record<string, unknown> = {};
                try {
                    secretData = JSON.parse(updateSecretValue);
                } catch {
                    secretData = { value: updateSecretValue };
                }

                await updateVaultSecret(updatingSecret.clientName, updatingSecret.label, {
                    clientName: updatingSecret.clientName,
                    label: updatingSecret.label,
                    data: secretData
                });

                setSecrets((prev) => prev.map((s) =>
                    s.id === updatingSecret.id
                        ? { ...s, version: s.version + 1, updatedAt: new Date().toISOString() }
                        : s
                ));

                toast({
                    title: "Secret Updated",
                    description: `Secret "${updatingSecret.label}" has been updated to version ${updatingSecret.version + 1}.`
                });
                setIsUpdateSecretOpen(false);
                setUpdatingSecret(null);
            } catch (error: any) {
                console.error("Failed to update secret", error);
                toast({
                    title: "Update failed",
                    description: error?.message || "Failed to update secret",
                    variant: "destructive"
                });
            }
        }
    };

    const openVersionHistory = (secret: VaultSecret) => {
        setVersionHistorySecret(secret);
        setIsVersionHistoryOpen(true);
    };

    const openDeleteSecret = (secret: VaultSecret) => {
        setDeletingSecret(secret);
        setIsDeleteSecretOpen(true);
    };

    const handleDeleteSecret = async () => {
        if (deletingSecret) {
            try {
                await deleteVaultSecret(deletingSecret.clientName, deletingSecret.label);
                setSecrets((prev) => prev.filter((s) => s.id !== deletingSecret.id));
                toast({
                    title: "Secret Deleted",
                    description: `Secret "${deletingSecret.label}" has been permanently deleted from the vault.`,
                    variant: "destructive"
                });
                setIsDeleteSecretOpen(false);
                setDeletingSecret(null);
            } catch (error: any) {
                console.error("Failed to delete secret", error);
                toast({
                    title: "Delete failed",
                    description: error?.message || "Failed to delete secret",
                    variant: "destructive"
                });
            }
        }
    };

    const openRevokeToken = (token: VaultToken) => {
        setRevokingToken(token);
        setIsRevokeTokenOpen(true);
    };

    const handleConfirmRevokeToken = async () => {
        if (revokingToken) {
            try {
                await revokeVaultTokenById(parseInt(revokingToken.id));
                toast({
                    title: "Token Revoked",
                    description: `Token for "${revokingToken.appName}" has been revoked and can no longer be used.`
                });
                setTokens(prev => prev.map(t =>
                    t.id === revokingToken.id ? { ...t, revoked: true } : t
                ));
            } catch (error: any) {
                toast({
                    title: "Failed to revoke token",
                    description: typeof error === "string" ? error : "Please try again.",
                    variant: "destructive"
                });
            } finally {
                setIsRevokeTokenOpen(false);
                setRevokingToken(null);
            }
        }
    };

    const handleExportAuditLogs = () => {
        const logData = JSON.stringify(filteredAuditLogs, null, 2);
        const blob = new Blob([logData], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `vault-audit-logs-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({
            title: "Logs Exported",
            description: `${filteredAuditLogs.length} audit log entries exported.`
        });
    };

    const formatLocalDate = (dateString: string | null): string => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch (e) {
            return "Invalid Date";
        }
    };

    const formatLocalDateTime = (dateString: string | null): string => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return "Invalid Date";
        }
    };

    const formatLocalTime = (dateString: string | null): string => {
        if (!dateString) return "Never";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";
            return date.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return "Invalid Date";
        }
    };


    const getEventTypeBadge = (eventType: string, success: boolean) => {
        if (!success) {
            return <VaultPill tone="danger" icon={XCircle}>Failed</VaultPill>;
        }

        const eventStyles: Record<string, { tone: VaultTone; icon: LucideIcon }> = {
            "secret_read": { tone: "primary", icon: Eye },
            "secret_update": { tone: "warning", icon: Edit },
            "secret_create": { tone: "success", icon: Plus },
            "secret_delete": { tone: "danger", icon: Trash2 },
            "token_create": { tone: "violet", icon: Key },
            "token_revoke": { tone: "orange", icon: Ban },
            "client_update": { tone: "muted", icon: Users },
            "app_create": { tone: "teal", icon: AppWindow },
            "auth_failed": { tone: "danger", icon: AlertTriangle },
        };

        const style = eventStyles[eventType] || { tone: "muted", icon: Activity };
        return (
            <VaultPill tone={style.tone} icon={style.icon}>
                {eventType.replace(/_/g, " ")}
            </VaultPill>
        );
    };

    const getExpiryStatus = (expiresAt: string, revoked: boolean, isExpired?: boolean, createdAt?: string) => {
        if (revoked) {
            return <VaultPill tone="danger" icon={XCircle}>Revoked</VaultPill>;
        }

        if (isExpired === true) {
            return <VaultPill tone="muted" icon={Clock}>Expired</VaultPill>;
        }

        try {
            const expiry = new Date(expiresAt);
            const now = createdAt ? new Date(createdAt) : new Date();
            if (isNaN(expiry.getTime())) {
                return <VaultPill tone="muted" icon={Clock}>Invalid Date</VaultPill>;
            }

            const hoursLeft = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);

            if (hoursLeft < 0 && isExpired !== false) {
                return <VaultPill tone="muted" icon={Clock}>Expired</VaultPill>;
            }
            if (hoursLeft < 1) {
                return <VaultPill tone="danger" icon={AlertTriangle}>{Math.round(hoursLeft * 60)}m left</VaultPill>;
            }
            if (hoursLeft < 6) {
                return <VaultPill tone="warning" icon={Clock}>{Math.round(hoursLeft)}h left</VaultPill>;
            }
            if (hoursLeft < 24) {
                return <VaultPill tone="warning" icon={Clock}>{Math.round(hoursLeft)}h left</VaultPill>;
            }
            return <VaultPill tone="success" icon={CheckCircle2}>Active</VaultPill>;
        } catch (e) {
            return <VaultPill tone="muted" icon={Clock}>Unknown</VaultPill>;
        }
    };

    const filteredClients = useMemo(() => {
        return clients.filter(client => {
            if (clientsFilter === "active" && !client.active) return false;
            if (clientsFilter === "inactive" && client.active) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return client.name.toLowerCase().includes(term) || client.description.toLowerCase().includes(term);
            }
            return true;
        });
    }, [clients, clientsFilter, searchTerm]);

    const filteredApps = useMemo(() => {
        return clientApps.filter(app => {
            if (appsFilter === "active" && !app.active) return false;
            if (appsFilter === "inactive" && app.active) return false;
            if (appsFilter !== "all" && appsFilter !== "active" && appsFilter !== "inactive" && app.clientId !== appsFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return app.name.toLowerCase().includes(term) || app.clientName.toLowerCase().includes(term);
            }
            return true;
        });
    }, [appsFilter, clientApps, searchTerm]);

    const filteredSecrets = useMemo(() => {
        return secrets.filter(secret => {
            if (secretsFilter !== "all" && secret.clientId !== secretsFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return secret.label.toLowerCase().includes(term) || secret.clientName.toLowerCase().includes(term);
            }
            return true;
        });
    }, [secrets, secretsFilter, searchTerm]);

    const filteredTokens = useMemo(() => {
        return tokens.filter(token => {
            if (tokensFilter === "active" && (
                token.revoked || (token.isExpired === true || (token.isExpired === undefined && new Date(token.expiresAt) < new Date()))
            )) return false;
            if (tokensFilter === "revoked" && !token.revoked) return false;
            if (tokensFilter === "expired" && (
                token.revoked || (token.isExpired === false || (token.isExpired === undefined && new Date(token.expiresAt) >= new Date()))
            )) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return token.appName.toLowerCase().includes(term) || token.clientName.toLowerCase().includes(term) || token.sourceIp.includes(term);
            }
            return true;
        });
    }, [searchTerm, tokens, tokensFilter]);

    const filteredAuditLogs = useMemo(() => {
        return auditLogs.filter(log => {
            if (auditEventFilter !== "all" && log.eventType !== auditEventFilter) return false;
            if (auditClientFilter !== "all" && log.clientId !== auditClientFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                return (
                    log.clientName.toLowerCase().includes(term) ||
                    (log.appName?.toLowerCase().includes(term)) ||
                    (log.secretLabel?.toLowerCase().includes(term)) ||
                    log.sourceIp.includes(term)
                );
            }
            return true;
        });
    }, [auditClientFilter, auditEventFilter, auditLogs, searchTerm]);

    const paginatedClients = useMemo(() => {
        const start = (clientsPage - 1) * ITEMS_PER_PAGE;
        return filteredClients.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredClients, clientsPage]);

    const paginatedApps = useMemo(() => {
        const start = (appsPage - 1) * ITEMS_PER_PAGE;
        return filteredApps.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredApps, appsPage]);

    const paginatedSecrets = useMemo(() => {
        const start = (secretsPage - 1) * ITEMS_PER_PAGE;
        return filteredSecrets.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredSecrets, secretsPage]);

    const paginatedTokens = useMemo(() => {
        const start = (tokensPage - 1) * ITEMS_PER_PAGE;
        return filteredTokens.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredTokens, tokensPage]);

    const paginatedAuditLogs = useMemo(() => {
        const start = (auditPage - 1) * ITEMS_PER_PAGE;
        return filteredAuditLogs.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredAuditLogs, auditPage]);

    const renderPagination = (currentPage: number, totalItems: number, setPage: (page: number) => void, label: string) => {
        if (totalItems === 0) return null;
        const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

        return (
            <div className="endpoint-table-pagination endpoint-pagination-bar border-t">
                <span className="endpoint-pagination-summary">
                    Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems.toLocaleString()} entries
                </span>
                <CardPagination
                    label={label}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setPage}
                />
            </div>
        );
    };

    const countLabel = (count: number, singular: string, plural: string) =>
        `${count.toLocaleString()} ${count === 1 ? singular : plural}`;

    return (
        <div className="endpoint-overview endpoint-data-sections vault-management">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="vault-tabs-root">
                <div className="overview-card vault-nav">
                    <TabsList unstyled aria-label="Vault sections" className="cea-tabs vault-tabs">
                        {VAULT_TABS.map(({ value, label, icon: TabIcon }) => (
                            <TabsTrigger
                                key={value}
                                unstyled
                                value={value}
                                className={`cea-tab ${activeTab === value ? "active" : ""}`}
                            >
                                <TabIcon className="shrink-0" />
                                {label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <span className="vault-connection" role="status">
                        <span className="vault-connection-dot" aria-hidden="true" />
                        Vault Connected
                    </span>
                </div>

                <TabsContent value="overview" className="vault-panel vault-panel-overview mt-0">
                    <div className="vault-stat-grid">
                        <VaultStatCard icon={Building2} label="Active Clients" value={stats.activeClients} />
                        <VaultStatCard icon={AppWindow} label="Active Apps" value={stats.activeApps} />
                        <VaultStatCard icon={Lock} label="Secrets Stored" value={stats.totalSecrets} tone="amber" />
                        <VaultStatCard icon={Key} label="Active Tokens" value={stats.activeTokens} tone="success" />
                        <VaultStatCard icon={Ban} label="Revoked Tokens" value={stats.revokedTokens} tone="orange" />
                        <VaultStatCard icon={AlertTriangle} label="Failed Events" value={stats.recentFailures} tone="danger" />
                    </div>

                    <div className="vault-insight-grid">
                        <Card className="overview-card vault-list-card">
                            <OverviewCardHeader
                                fromdetails
                                title="Recent Activity"
                                description="Latest events recorded by the vault"
                                icon={Activity}
                            />
                            <CardContent className="overview-card-content">
                                {auditLogs.length === 0 ? (
                                    <OverviewEmptyState icon={Activity} message="No recent vault activity." />
                                ) : (
                                    <div className="vault-list">
                                        {auditLogs.slice(0, 5).map((log) => (
                                            <div key={log.id} className="vault-list-row vault-activity-row">
                                                <div className="vault-list-copy">
                                                    <p className="vault-list-title">{log.clientName}</p>
                                                    <p className="vault-list-sub">{log.secretLabel || log.appName || "System"}</p>
                                                </div>
                                                {getEventTypeBadge(log.eventType, log.success)}
                                                <span className="vault-list-time">{formatLocalTime(log.timestamp)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="overview-card vault-list-card">
                            <OverviewCardHeader
                                fromdetails
                                title="Most Accessed Secrets"
                                description="Secrets with the highest read volume"
                                icon={Lock}
                            />
                            <CardContent className="overview-card-content">
                                {secrets.length === 0 ? (
                                    <OverviewEmptyState icon={Lock} message="No secrets stored yet." />
                                ) : (
                                    <div className="vault-list">
                                        {[...secrets].sort((a, b) => b.accessCount - a.accessCount).slice(0, 5).map((secret, index) => (
                                            <div key={secret.id} className="vault-list-row vault-secret-row">
                                                <span className="vault-rank">{index + 1}</span>
                                                <div className="vault-list-copy">
                                                    <p className="vault-list-title" data-mono="true">{secret.label}</p>
                                                    <p className="vault-list-sub">{secret.clientName}</p>
                                                </div>
                                                <div className="vault-list-metric">
                                                    <strong>{secret.accessCount.toLocaleString()}</strong>
                                                    <span>accesses</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="clients" className="vault-panel mt-0">
                    <div className="overview-card endpoint-section-toolbar">
                        <div className="endpoint-toolbar-fields flex items-center">
                            <VaultSearchField
                                placeholder="Search clients..."
                                value={searchTerm}
                                onChange={(value) => { setSearchTerm(value); setClientsPage(1); }}
                            />
                            <VaultFilterField icon={Filter} label="Status">
                                <Select value={clientsFilter} onValueChange={(v) => { setClientsFilter(v); setClientsPage(1); }}>
                                    <SelectTrigger className="vault-select" aria-label="Filter clients by status">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Status</SelectItem>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="inactive">Inactive</SelectItem>
                                    </SelectContent>
                                </Select>
                            </VaultFilterField>
                        </div>
                        <div className="endpoint-toolbar-actions flex items-center">
                            <Button variant="outline" onClick={handleRefreshTab} disabled={isLoadingClients}>
                                <RefreshCw className={isLoadingClients ? "animate-spin" : undefined} />
                                Refresh
                            </Button>
                            <EndpointActionButton onClick={() => setIsCreateClientOpen(true)}>
                                <Plus className="w-4 h-4" />
                                Create Client
                            </EndpointActionButton>
                        </div>
                    </div>

                    <Card className="overview-card endpoint-section-table-card vault-table-card">
                        <OverviewCardHeader
                            fromdetails
                            title="Clients"
                            description="Client groups that organize apps and their credentials"
                            icon={Building2}
                            detail={isLoadingClients ? undefined : countLabel(filteredClients.length, "client", "clients")}
                        />
                        <CardContent className="overview-card-content endpoint-table-content">
                            <div key={clientsPage} className="vault-table-scroll">
                                <Table className="endpoint-data-table vault-table vault-table-actions w-full">
                                    <TableHeader className="sticky top-0 z-10">
                                        <TableRow>
                                            <TableHead>Client Name</TableHead>
                                            <TableHead>Description</TableHead>
                                            <TableHead>Apps</TableHead>
                                            <TableHead>Secrets</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Created</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingClients ? (
                                            <VaultTableState colSpan={7} icon={Building2} loading message="Loading clients..." />
                                        ) : paginatedClients.length === 0 ? (
                                            <VaultTableState colSpan={7} icon={Building2} message="No clients found. Create a client to get started." />
                                        ) : paginatedClients.map((client) => (
                                            <TableRow key={client.id}>
                                                <TableCell>
                                                    <div className="vault-identity">
                                                        <span className="vault-row-icon" aria-hidden="true"><Building2 /></span>
                                                        <span className="vault-identity-name">{client.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="vault-cell-muted vault-cell-clamp" title={client.description}>
                                                        {client.description || "—"}
                                                    </span>
                                                </TableCell>
                                                <TableCell><span className="vault-count">{client.appsCount}</span></TableCell>
                                                <TableCell><span className="vault-count">{client.secretsCount}</span></TableCell>
                                                <TableCell><VaultStatus active={client.active} /></TableCell>
                                                <TableCell className="vault-cell-muted">{formatLocalDate(client.createdAt)}</TableCell>
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="vault-row-menu no-default-hover-elevate" aria-label={`Actions for ${client.name}`}>
                                                                <MoreVertical />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="vault-menu">
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openEditClient(client)}>
                                                                <Edit />
                                                                Edit Client
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openDeactivateClient(client)}>
                                                                {client.active ? <XCircle /> : <CheckCircle2 />}
                                                                {client.active ? "Deactivate" : "Activate"}
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator className="vault-menu-separator" />
                                                            <DropdownMenuItem className="vault-menu-item" data-tone="danger" onClick={() => openDeleteClient(client)}>
                                                                <Trash2 />
                                                                Delete Client
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                        {renderPagination(clientsPage, filteredClients.length, setClientsPage, "Client pages")}
                    </Card>
                </TabsContent>

                <TabsContent value="apps" className="vault-panel mt-0">
                    <div className="overview-card endpoint-section-toolbar">
                        <div className="endpoint-toolbar-fields flex items-center">
                            <VaultSearchField
                                placeholder="Search apps..."
                                value={searchTerm}
                                onChange={(value) => { setSearchTerm(value); setAppsPage(1); }}
                            />
                            <VaultFilterField icon={Filter} label="Filter">
                                <Select value={appsFilter} onValueChange={(v) => { setAppsFilter(v); setAppsPage(1); }}>
                                    <SelectTrigger className="vault-select" aria-label="Filter apps">
                                        <SelectValue placeholder="Filter" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Apps</SelectItem>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="inactive">Inactive</SelectItem>
                                        {clients.map(c => (
                                            <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </VaultFilterField>
                        </div>
                        <div className="endpoint-toolbar-actions flex items-center">
                            <Button variant="outline" onClick={handleRefreshTab} disabled={isLoadingApps}>
                                <RefreshCw className={isLoadingApps ? "animate-spin" : undefined} />
                                Refresh
                            </Button>
                            <EndpointActionButton onClick={() => setIsCreateAppOpen(true)}>
                                <Plus className="w-4 h-4" />
                                Create App
                            </EndpointActionButton>
                        </div>
                    </div>

                    <Card className="overview-card endpoint-section-table-card vault-table-card">
                        <OverviewCardHeader
                            fromdetails
                            title="Client Apps"
                            description="Applications that authenticate and access vault secrets"
                            icon={AppWindow}
                            detail={isLoadingApps ? undefined : countLabel(filteredApps.length, "app", "apps")}
                        />
                        <CardContent className="overview-card-content endpoint-table-content">
                            <div key={appsPage} className="vault-table-scroll">
                                <Table className="endpoint-data-table vault-table vault-table-actions w-full">
                                    <TableHeader className="sticky top-0 z-10">
                                        <TableRow>
                                            <TableHead>App Name</TableHead>
                                            <TableHead>Client</TableHead>
                                            <TableHead>Client Secret</TableHead>
                                            <TableHead>Scopes</TableHead>
                                            <TableHead>Allowed Labels</TableHead>
                                            <TableHead>Active Tokens</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingApps ? (
                                            <VaultTableState colSpan={8} icon={AppWindow} loading message="Loading apps..." />
                                        ) : paginatedApps.length === 0 ? (
                                            <VaultTableState colSpan={8} icon={AppWindow} message="No apps found. Create an app to get started." />
                                        ) : paginatedApps.map((app) => (
                                            <TableRow key={app.id}>
                                                <TableCell>
                                                    <div className="vault-identity">
                                                        <span className="vault-row-icon" data-tone="indigo" aria-hidden="true"><AppWindow /></span>
                                                        <span className="vault-identity-name" data-mono="true">{app.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="vault-cell-strong">{app.clientName}</TableCell>
                                                <TableCell>
                                                    <div className="vault-secret-cell">
                                                        <code className="vault-code">
                                                            {showSecret === app.id ? app.clientSecret : "••••••••••••••••"}
                                                        </code>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="vault-inline-button no-default-hover-elevate"
                                                            aria-label={showSecret === app.id ? "Hide client secret" : "Show client secret"}
                                                            onClick={() => setShowSecret(showSecret === app.id ? null : app.id)}
                                                        >
                                                            {showSecret === app.id ? <EyeOff /> : <Eye />}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="vault-inline-button no-default-hover-elevate"
                                                            aria-label="Copy client secret"
                                                            onClick={() => copyToClipboard(app.clientSecret, "Client secret")}
                                                        >
                                                            <Copy />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                                <TableCell><VaultScopes scopes={app.scopes} /></TableCell>
                                                <TableCell>
                                                    <div className="vault-chip-list" data-limit="true">
                                                        {app.allowedLabels.slice(0, 3).map(label => (
                                                            <span key={label} className="vault-tag">{label}</span>
                                                        ))}
                                                        {app.allowedLabels.length > 3 && (
                                                            <span className="vault-tag">+{app.allowedLabels.length - 3}</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell><span className="vault-count">{app.activeTokens}</span></TableCell>
                                                <TableCell><VaultStatus active={app.active} /></TableCell>
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="vault-row-menu no-default-hover-elevate" aria-label={`Actions for ${app.name}`}>
                                                                <MoreVertical />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="vault-menu">
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => { setViewingApp(app); setIsViewCredentialsOpen(true); setShowViewingSecret(false); }}>
                                                                <Key />
                                                                View Credentials
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openEditApp(app)}>
                                                                <Edit />
                                                                Edit App
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openRegenerateSecret(app)}>
                                                                <RefreshCw />
                                                                Regenerate Secret
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openRevokeAllTokens(app)}>
                                                                <Ban />
                                                                Revoke All Tokens
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openDeactivateApp(app)}>
                                                                {app.active ? <XCircle /> : <CheckCircle2 />}
                                                                {app.active ? "Deactivate" : "Activate"}
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator className="vault-menu-separator" />
                                                            <DropdownMenuItem className="vault-menu-item" data-tone="danger" onClick={() => openDeleteApp(app)}>
                                                                <Trash2 />
                                                                Delete App
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                        {renderPagination(appsPage, filteredApps.length, setAppsPage, "App pages")}
                    </Card>
                </TabsContent>

                <TabsContent value="secrets" className="vault-panel mt-0">
                    <div className="overview-card endpoint-section-toolbar">
                        <div className="endpoint-toolbar-fields flex items-center">
                            <VaultSearchField
                                placeholder="Search secrets by label..."
                                value={searchTerm}
                                onChange={(value) => { setSearchTerm(value); setSecretsPage(1); }}
                            />
                            <VaultFilterField icon={Building2} label="Client">
                                <Select value={secretsFilter} onValueChange={(v) => { setSecretsFilter(v); setSecretsPage(1); }}>
                                    <SelectTrigger className="vault-select" aria-label="Filter secrets by client">
                                        <SelectValue placeholder="Filter by client" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Clients</SelectItem>
                                        {clients.map(c => (
                                            <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </VaultFilterField>
                        </div>
                        <div className="endpoint-toolbar-actions flex items-center">
                            <Button variant="outline" onClick={handleRefreshTab} disabled={isLoadingSecrets}>
                                <RefreshCw className={isLoadingSecrets ? "animate-spin" : undefined} />
                                Refresh
                            </Button>
                            <EndpointActionButton onClick={() => setIsCreateSecretOpen(true)}>
                                <Plus className="w-4 h-4" />
                                Create Secret
                            </EndpointActionButton>
                        </div>
                    </div>

                    <Card className="overview-card endpoint-section-table-card vault-table-card">
                        <OverviewCardHeader
                            fromdetails
                            title="Secrets"
                            description="Encrypted values stored in the vault"
                            icon={Lock}
                            detail={isLoadingSecrets ? undefined : countLabel(filteredSecrets.length, "secret", "secrets")}
                        />
                        <CardContent className="overview-card-content endpoint-table-content">
                            <div key={secretsPage} className="vault-table-scroll">
                                <Table className="endpoint-data-table vault-table vault-table-actions w-full">
                                    <TableHeader className="sticky top-0 z-10">
                                        <TableRow>
                                            <TableHead>Label</TableHead>
                                            <TableHead>Client</TableHead>
                                            <TableHead>Secret</TableHead>
                                            <TableHead>Vault Path</TableHead>
                                            <TableHead>Version</TableHead>
                                            <TableHead>Access Count</TableHead>
                                            <TableHead>Last Updated</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingSecrets ? (
                                            <VaultTableState colSpan={8} icon={Lock} loading message="Loading secrets..." />
                                        ) : paginatedSecrets.length === 0 ? (
                                            <VaultTableState colSpan={8} icon={Lock} message="No secrets found. Create a secret to get started." />
                                        ) : paginatedSecrets.map((secret) => (
                                            <TableRow key={secret.id}>
                                                <TableCell>
                                                    <div className="vault-identity">
                                                        <span className="vault-row-icon" data-tone="amber" aria-hidden="true"><Lock /></span>
                                                        <span className="vault-identity-name" data-mono="true">{secret.label}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="vault-cell-strong">{secret.clientName}</TableCell>
                                                <TableCell>
                                                    <code className="vault-code" data-muted="true">
                                                        {secret.appSecret || "••••••••••••••••"}
                                                    </code>
                                                </TableCell>
                                                <TableCell>
                                                    <code className="vault-code" data-muted="true">
                                                        {secret.vaultPath.trim()}
                                                    </code>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="vault-tag" data-mono="true">v{secret.version}</span>
                                                </TableCell>
                                                <TableCell className="vault-cell-strong">{secret.accessCount.toLocaleString()}</TableCell>
                                                <TableCell className="vault-cell-muted">{formatLocalDate(secret.updatedAt)}</TableCell>
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="vault-row-menu no-default-hover-elevate" aria-label={`Actions for ${secret.label}`}>
                                                                <MoreVertical />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="vault-menu">
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openViewSecret(secret)}>
                                                                <Eye />
                                                                View Secret
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openUpdateSecret(secret)}>
                                                                <Edit />
                                                                Update Secret
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="vault-menu-item" onClick={() => openVersionHistory(secret)}>
                                                                <History />
                                                                Version History
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator className="vault-menu-separator" />
                                                            <DropdownMenuItem className="vault-menu-item" data-tone="danger" onClick={() => openDeleteSecret(secret)}>
                                                                <Trash2 />
                                                                Delete Secret
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                        {renderPagination(secretsPage, filteredSecrets.length, setSecretsPage, "Secret pages")}
                    </Card>
                </TabsContent>

                <TabsContent value="tokens" className="vault-panel mt-0">
                    <div className="overview-card endpoint-section-toolbar">
                        <div className="endpoint-toolbar-fields flex items-center">
                            <VaultSearchField
                                placeholder="Search by app or IP..."
                                value={searchTerm}
                                onChange={(value) => { setSearchTerm(value); setTokensPage(1); }}
                            />
                            <VaultFilterField icon={Filter} label="Status">
                                <Select value={tokensFilter} onValueChange={(v) => { setTokensFilter(v); setTokensPage(1); }}>
                                    <SelectTrigger className="vault-select" aria-label="Filter tokens by status">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Tokens</SelectItem>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="revoked">Revoked</SelectItem>
                                        <SelectItem value="expired">Expired</SelectItem>
                                    </SelectContent>
                                </Select>
                            </VaultFilterField>
                        </div>
                        <div className="endpoint-toolbar-actions flex items-center">
                            <Button variant="outline" onClick={handleRefreshTab} disabled={isLoadingTokens}>
                                <RefreshCw className={isLoadingTokens ? "animate-spin" : undefined} />
                                Refresh
                            </Button>
                            <Button variant="outline" className="vault-warning-action" onClick={handleRevokeAllExpired}>
                                <Ban />
                                Revoke All Expired
                            </Button>
                        </div>
                    </div>

                    <Card className="overview-card endpoint-section-table-card vault-table-card">
                        <OverviewCardHeader
                            fromdetails
                            title="Tokens"
                            description="Short-lived access tokens issued to client apps"
                            icon={KeyRound}
                            detail={isLoadingTokens ? undefined : countLabel(filteredTokens.length, "token", "tokens")}
                        />
                        <CardContent className="overview-card-content endpoint-table-content">
                            <div key={tokensPage} className="vault-table-scroll">
                                <Table className="endpoint-data-table vault-table vault-table-actions w-full">
                                    <TableHeader className="sticky top-0 z-10">
                                        <TableRow>
                                            <TableHead>App</TableHead>
                                            <TableHead>Client</TableHead>
                                            <TableHead>Token (Preview)</TableHead>
                                            <TableHead>Source IP</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Created</TableHead>
                                            <TableHead>Last Used</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingTokens ? (
                                            <VaultTableState colSpan={8} icon={KeyRound} loading message="Loading tokens..." />
                                        ) : paginatedTokens.length === 0 ? (
                                            <VaultTableState colSpan={8} icon={KeyRound} message="No tokens yet. Issue one from the Apps tab." />
                                        ) : paginatedTokens.map((token) => (
                                            <TableRow key={token.id}>
                                                <TableCell>
                                                    <div className="vault-identity">
                                                        <span className="vault-row-icon" data-tone="indigo" aria-hidden="true"><KeyRound /></span>
                                                        <span className="vault-identity-name" data-mono="true">{token.appName}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="vault-cell-strong">{token.clientName}</TableCell>
                                                <TableCell>
                                                    <code className="vault-code">{token.token}</code>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="vault-mono-text">{token.sourceIp}</span>
                                                </TableCell>
                                                <TableCell>
                                                    {getExpiryStatus(token.expiresAt, token.revoked, token.isExpired, token.createdAt)}
                                                </TableCell>
                                                <TableCell className="vault-cell-muted">{formatLocalDateTime(token.createdAt)}</TableCell>
                                                <TableCell className="vault-cell-muted">
                                                    {token.lastUsedAt ? formatLocalTime(token.lastUsedAt) : "Never"}
                                                </TableCell>
                                                <TableCell>
                                                    {!token.revoked && ((token.isExpired === false) || (token.isExpired === undefined && new Date(token.expiresAt) > new Date())) && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="vault-row-action no-default-hover-elevate"
                                                            onClick={() => openRevokeToken(token)}
                                                        >
                                                            <Ban />
                                                            Revoke
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                        {renderPagination(tokensPage, filteredTokens.length, setTokensPage, "Token pages")}
                    </Card>
                </TabsContent>

                <TabsContent value="audit" className="vault-panel mt-0">
                    <div className="overview-card endpoint-section-toolbar">
                        <div className="endpoint-toolbar-fields flex items-center">
                            <VaultSearchField
                                placeholder="Search logs..."
                                value={searchTerm}
                                onChange={(value) => { setSearchTerm(value); setAuditPage(1); }}
                            />
                            <VaultFilterField icon={Activity} label="Event type">
                                <Select value={auditEventFilter} onValueChange={(v) => { setAuditEventFilter(v); setAuditPage(1); }}>
                                    <SelectTrigger className="vault-select" aria-label="Filter logs by event type">
                                        <SelectValue placeholder="Event Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Events</SelectItem>
                                        <SelectItem value="secret_read">Secret Read</SelectItem>
                                        <SelectItem value="secret_update">Secret Update</SelectItem>
                                        <SelectItem value="secret_create">Secret Create</SelectItem>
                                        <SelectItem value="secret_delete">Secret Delete</SelectItem>
                                        <SelectItem value="token_create">Token Create</SelectItem>
                                        <SelectItem value="token_revoke">Token Revoke</SelectItem>
                                        <SelectItem value="auth_failed">Auth Failed</SelectItem>
                                    </SelectContent>
                                </Select>
                            </VaultFilterField>
                            <VaultFilterField icon={Building2} label="Client">
                                <Select value={auditClientFilter} onValueChange={(v) => { setAuditClientFilter(v); setAuditPage(1); }}>
                                    <SelectTrigger className="vault-select" aria-label="Filter logs by client">
                                        <SelectValue placeholder="Client" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Clients</SelectItem>
                                        {clients.map(c => (
                                            <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </VaultFilterField>
                        </div>
                        <div className="endpoint-toolbar-actions flex items-center">
                            <Button variant="outline" onClick={handleRefreshTab} disabled={isLoadingAuditLogs}>
                                <RefreshCw className={isLoadingAuditLogs ? "animate-spin" : undefined} />
                                Refresh
                            </Button>
                            <Button variant="outline" onClick={handleExportAuditLogs}>
                                <Download />
                                Export Logs
                            </Button>
                        </div>
                    </div>

                    <Card className="overview-card endpoint-section-table-card vault-table-card">
                        <OverviewCardHeader
                            fromdetails
                            title="Audit Logs"
                            description="Every vault access, change and authentication event"
                            icon={ScrollText}
                            detail={isLoadingAuditLogs ? undefined : countLabel(filteredAuditLogs.length, "entry", "entries")}
                        />
                        <CardContent className="overview-card-content endpoint-table-content">
                            <div key={auditPage} className="vault-table-scroll">
                                <Table className="endpoint-data-table vault-table w-full">
                                    <TableHeader className="sticky top-0 z-10">
                                        <TableRow>
                                            <TableHead>Timestamp</TableHead>
                                            <TableHead>Event</TableHead>
                                            <TableHead>User</TableHead>
                                            <TableHead>Client / App</TableHead>
                                            <TableHead>Target</TableHead>
                                            <TableHead>Source IP</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingAuditLogs && paginatedAuditLogs.length === 0 ? (
                                            <VaultTableState colSpan={6} icon={ScrollText} loading message="Loading audit logs..." />
                                        ) : paginatedAuditLogs.length === 0 ? (
                                            <VaultTableState colSpan={6} icon={ScrollText} message="No audit logs found." />
                                        ) : paginatedAuditLogs.map((log) => (
                                            <TableRow key={log.id} data-failed={!log.success}>
                                                <TableCell className="vault-cell-muted">{formatLocalDateTime(log.timestamp)}</TableCell>
                                                <TableCell>{getEventTypeBadge(log.eventType, log.success)}</TableCell>
                                                <TableCell>
                                                    <span className="vault-mono-text" data-muted="true">{log.principalName}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="vault-cell-stack">
                                                        <span className="vault-cell-strong">{log.clientName}</span>
                                                        {log.appName && (
                                                            <span className="vault-mono-text" data-muted="true">{log.appName}</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {log.secretLabel ? (
                                                        <code className="vault-code">{log.secretLabel}</code>
                                                    ) : (
                                                        <span className="vault-cell-muted">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="vault-mono-text">{log.sourceIp}</span>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                        {renderPagination(auditPage, filteredAuditLogs.length, setAuditPage, "Audit log pages")}
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={isCreateClientOpen} onOpenChange={setIsCreateClientOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={Building2}
                        title="Create Client Group"
                        subtitle="Create a client group to organize apps. Apps under this group will have their own credentials."
                    />
                    <div className="vault-dialog-body">
                        <div className="vault-field">
                            <Label htmlFor="client-name" className="vault-field-label">Client Name</Label>
                            <Input
                                id="client-name"
                                placeholder="e.g., Payment Gateway"
                                value={newClientName}
                                onChange={(e) => setNewClientName(e.target.value)}
                            />
                        </div>
                        <div className="vault-field">
                            <Label htmlFor="client-desc" className="vault-field-label">Description</Label>
                            <Textarea
                                id="client-desc"
                                placeholder="Brief description of this client's purpose..."
                                value={newClientDescription}
                                onChange={(e) => setNewClientDescription(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsCreateClientOpen(false)}>Cancel</Button>
                        <Button className="vault-dialog-button" data-tone="primary" onClick={handleCreateClient} disabled={isSavingClient || !newClientName.trim()}>
                            Create Client
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isCreateAppOpen} onOpenChange={setIsCreateAppOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={AppWindow}
                        title="Create New App"
                        subtitle="Register an application that will authenticate and access secrets."
                    />
                    <div className="vault-dialog-body">
                        <div className="vault-field">
                            <Label htmlFor="app-client" className="vault-field-label">Client</Label>
                            <Select value={newAppClientId} onValueChange={setNewAppClientId}>
                                <SelectTrigger id="app-client">
                                    <SelectValue placeholder="Select a client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.filter(c => c.active).map(c => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="vault-field">
                            <Label htmlFor="app-name" className="vault-field-label">App Name</Label>
                            <Input
                                id="app-name"
                                placeholder="e.g., payment-api-prod"
                                value={newAppName}
                                onChange={(e) => setNewAppName(e.target.value)}
                            />
                        </div>
                        <div className="vault-field">
                            <span className="vault-field-label">Scopes</span>
                            <div className="vault-option-group">
                                {APP_SCOPES.map((scope) => (
                                    <label key={scope} className="vault-option" data-tone={scope === "delete" ? "danger" : undefined}>
                                        <Checkbox
                                            id={`scope-${scope}`}
                                            checked={newAppScopes.includes(scope)}
                                            onCheckedChange={(checked) => {
                                                if (checked) setNewAppScopes([...newAppScopes, scope]);
                                                else setNewAppScopes(newAppScopes.filter(s => s !== scope));
                                            }}
                                        />
                                        {scope}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="vault-field">
                            <Label htmlFor="app-labels" className="vault-field-label">Allowed Labels</Label>
                            <Input
                                id="app-labels"
                                placeholder="db, api, internal (comma-separated)"
                                value={newAppLabels}
                                onChange={(e) => setNewAppLabels(e.target.value)}
                            />
                            <p className="vault-field-hint">Labels this app can access. Separate multiple labels with commas.</p>
                        </div>
                    </div>
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsCreateAppOpen(false)}>Cancel</Button>
                        <Button className="vault-dialog-button" data-tone="primary" onClick={handleCreateApp} disabled={isSavingApp || !newAppClientId.trim() || !newAppName.trim() || newAppScopes.length === 0}>
                            Create App
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isCreateSecretOpen} onOpenChange={setIsCreateSecretOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={Lock}
                        tone="warning"
                        title="Create New Secret"
                        subtitle="Store a new secret in the vault. The secret will be encrypted at rest."
                    />
                    <div className="vault-dialog-body">
                        <div className="vault-field">
                            <Label htmlFor="secret-client" className="vault-field-label">Client</Label>
                            <Select value={newSecretClientId} onValueChange={setNewSecretClientId}>
                                <SelectTrigger id="secret-client">
                                    <SelectValue placeholder="Select a client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.filter(c => c.active).map(c => (
                                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="vault-field">
                            <Label htmlFor="secret-label" className="vault-field-label">Secret Label</Label>
                            <Input
                                id="secret-label"
                                placeholder="e.g., prod/payments/db_master"
                                value={newSecretLabel}
                                onChange={(e) => setNewSecretLabel(e.target.value)}
                            />
                            <p className="vault-field-hint">Use path-style labels for organization (e.g., env/service/name)</p>
                        </div>
                        <div className="vault-field">
                            <Label htmlFor="secret-value" className="vault-field-label">Secret Value</Label>
                            <Textarea
                                id="secret-value"
                                placeholder="Enter the secret value..."
                                value={newSecretValue}
                                onChange={(e) => setNewSecretValue(e.target.value)}
                                className="font-mono"
                            />
                        </div>
                    </div>
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsCreateSecretOpen(false)}>Cancel</Button>
                        <Button className="vault-dialog-button" data-tone="primary" onClick={handleCreateSecret} disabled={isSavingSecret || !newSecretClientId.trim() || !newSecretLabel.trim() || !newSecretValue.trim()}>
                            Store Secret
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isCredentialsDialogOpen} onOpenChange={setIsCredentialsDialogOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={CheckCircle2}
                        tone="success"
                        title="App Created Successfully"
                        subtitle="Save these credentials now. The secret will not be shown again."
                    />
                    {generatedCredentials && (
                        <div className="vault-dialog-body">
                            <div className="vault-callout" data-tone="warning">
                                <AlertTriangle aria-hidden="true" />
                                <div>
                                    <p className="vault-callout-title">Important: Save these credentials</p>
                                    <p>Store these securely in your application configuration.</p>
                                </div>
                            </div>

                            <div className="vault-field">
                                <span className="vault-field-label">Client Name</span>
                                <p className="vault-field-text">{generatedCredentials.clientName}</p>
                            </div>

                            <VaultValueField label="Client ID" value={generatedCredentials.appId}>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="vault-icon-button no-default-hover-elevate"
                                    aria-label="Copy client ID"
                                    onClick={() => copyToClipboard(generatedCredentials.appId || "", "App ID")}
                                >
                                    <Copy />
                                </Button>
                            </VaultValueField>

                            <VaultValueField label="Client Secret" value={showGeneratedSecret ? generatedCredentials.clientSecret : MASKED_SECRET}>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="vault-icon-button no-default-hover-elevate"
                                    aria-label={showGeneratedSecret ? "Hide client secret" : "Show client secret"}
                                    onClick={() => setShowGeneratedSecret(!showGeneratedSecret)}
                                >
                                    {showGeneratedSecret ? <EyeOff /> : <Eye />}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="vault-icon-button no-default-hover-elevate"
                                    aria-label="Copy client secret"
                                    onClick={() => copyToClipboard(generatedCredentials.clientSecret, "Client Secret")}
                                >
                                    <Copy />
                                </Button>
                            </VaultValueField>

                            <div className="vault-callout">
                                <KeyRound aria-hidden="true" />
                                <div>
                                    <p className="vault-callout-title">Authentication Flow</p>
                                    <ol>
                                        <li>Use Client ID + Secret to request a short-lived access token</li>
                                        <li>Include the access token in API requests</li>
                                        <li>Access only secrets where label matches your allowed labels</li>
                                        <li>Client must refresh token before expiry</li>
                                    </ol>
                                    <p>Example: If allowed labels = ["db", "api"], you can access secrets like "prod/db/master" or "prod/api/key"</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button
                            className="vault-dialog-button"
                            data-tone="primary"
                            onClick={() => {
                                setIsCredentialsDialogOpen(false);
                                setGeneratedCredentials(null);
                                toast({
                                    title: "Client Ready",
                                    description: "Client credentials have been generated. Configure your app now."
                                });
                            }}
                        >
                            I've Saved the Credentials
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isViewCredentialsOpen} onOpenChange={setIsViewCredentialsOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={Key}
                        title="App Credentials"
                        subtitle="Credentials for authenticating this application with the Vault Service."
                    />
                    {viewingApp && (
                        <div className="vault-dialog-body">
                            <div className="vault-detail-grid">
                                <div className="vault-field">
                                    <span className="vault-field-label">App Name</span>
                                    <p className="vault-field-text" data-mono="true">{viewingApp.name}</p>
                                </div>
                                <div className="vault-field">
                                    <span className="vault-field-label">Client Group</span>
                                    <p className="vault-field-text">{viewingApp.clientName}</p>
                                </div>
                            </div>

                            <VaultValueField label="Client ID" value={viewingApp.appId}>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="vault-icon-button no-default-hover-elevate"
                                    aria-label="Copy client ID"
                                    onClick={() => copyToClipboard(viewingApp.clientId, "Client ID")}
                                >
                                    <Copy />
                                </Button>
                            </VaultValueField>

                            <VaultValueField label="Client Secret" value={showViewingSecret ? viewingApp.clientSecret : MASKED_SECRET}>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="vault-icon-button no-default-hover-elevate"
                                    aria-label={showViewingSecret ? "Hide client secret" : "Show client secret"}
                                    onClick={() => setShowViewingSecret(!showViewingSecret)}
                                >
                                    {showViewingSecret ? <EyeOff /> : <Eye />}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="vault-icon-button no-default-hover-elevate"
                                    aria-label="Copy client secret"
                                    onClick={() => copyToClipboard(viewingApp.clientSecret, "Client Secret")}
                                >
                                    <Copy />
                                </Button>
                            </VaultValueField>

                            <div className="vault-field">
                                <span className="vault-field-label">Scopes</span>
                                <VaultScopes scopes={viewingApp.scopes} />
                            </div>

                            <div className="vault-field">
                                <span className="vault-field-label">Allowed Labels</span>
                                <div className="vault-chip-list">
                                    {viewingApp.allowedLabels.map(label => (
                                        <span key={label} className="vault-tag">{label}</span>
                                    ))}
                                </div>
                                <p className="vault-field-hint">This app can access secrets where the label contains any of these values.</p>
                            </div>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsViewCredentialsOpen(false)}>
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isEditClientOpen} onOpenChange={setIsEditClientOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={Edit}
                        title="Edit Client Group"
                        subtitle="Update the client group name and description."
                    />
                    <div className="vault-dialog-body">
                        <div className="vault-field">
                            <Label htmlFor="editClientName" className="vault-field-label">Client Name</Label>
                            <Input
                                id="editClientName"
                                value={editClientName}
                                onChange={(e) => setEditClientName(e.target.value)}
                                placeholder="e.g., Payment Gateway"
                            />
                        </div>
                        <div className="vault-field">
                            <Label htmlFor="editClientDescription" className="vault-field-label">Description</Label>
                            <Textarea
                                id="editClientDescription"
                                value={editClientDescription}
                                onChange={(e) => setEditClientDescription(e.target.value)}
                                placeholder="Describe the purpose of this client group..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsEditClientOpen(false)}>Cancel</Button>
                        <Button className="vault-dialog-button" data-tone="primary" onClick={handleEditClient}>Save Changes</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeleteClientOpen} onOpenChange={setIsDeleteClientOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={AlertTriangle}
                        tone="danger"
                        title="Delete Client Group"
                        subtitle="This action cannot be undone. This will permanently delete the client group, all its apps, and revoke all associated tokens."
                    />
                    {deletingClient && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={Building2} tone="danger" name={deletingClient.name} sub={deletingClient.description}>
                                <span className="vault-tag">{deletingClient.appsCount} app(s)</span>
                                <span className="vault-tag">{deletingClient.secretsCount} secret(s)</span>
                            </VaultEntity>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsDeleteClientOpen(false)}>Cancel</Button>
                        <Button variant="destructive" className="vault-dialog-button" onClick={handleDeleteClient}>
                            <Trash2 />
                            Delete Client
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isEditAppOpen} onOpenChange={setIsEditAppOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={Edit}
                        title="Edit App"
                        subtitle="Update the app configuration and access policies."
                    />
                    <div className="vault-dialog-body">
                        <div className="vault-field">
                            <Label htmlFor="editAppName" className="vault-field-label">App Name</Label>
                            <Input
                                id="editAppName"
                                value={editAppName}
                                onChange={(e) => setEditAppName(e.target.value)}
                                placeholder="e.g., payment-api-prod"
                            />
                        </div>
                        <div className="vault-field">
                            <span className="vault-field-label">Scopes</span>
                            <div className="vault-option-group">
                                {APP_SCOPES.map((scope) => (
                                    <label key={scope} className="vault-option" data-tone={scope === "delete" ? "danger" : undefined}>
                                        <Checkbox
                                            checked={editAppScopes.includes(scope)}
                                            onCheckedChange={(checked) => {
                                                if (checked) {
                                                    setEditAppScopes([...editAppScopes, scope]);
                                                } else {
                                                    setEditAppScopes(editAppScopes.filter(s => s !== scope));
                                                }
                                            }}
                                        />
                                        {scope}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="vault-field">
                            <Label htmlFor="editAppLabels" className="vault-field-label">Allowed Labels (comma-separated)</Label>
                            <Input
                                id="editAppLabels"
                                value={editAppLabels}
                                onChange={(e) => setEditAppLabels(e.target.value)}
                                placeholder="e.g., prod/payments, prod/stripe"
                            />
                            <p className="vault-field-hint">App can access secrets matching any of these label patterns.</p>
                        </div>
                    </div>
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsEditAppOpen(false)}>Cancel</Button>
                        <Button className="vault-dialog-button" data-tone="primary" onClick={handleEditApp}>Save Changes</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeleteAppOpen} onOpenChange={setIsDeleteAppOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={AlertTriangle}
                        tone="danger"
                        title="Delete App"
                        subtitle="This action cannot be undone. This will permanently delete the app and revoke all its tokens."
                    />
                    {deletingApp && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={AppWindow} tone="danger" name={deletingApp.name} mono sub={`Client: ${deletingApp.clientName}`}>
                                <span className="vault-tag">{deletingApp.activeTokens} active token(s) will be revoked</span>
                            </VaultEntity>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsDeleteAppOpen(false)}>Cancel</Button>
                        <Button variant="destructive" className="vault-dialog-button" onClick={handleDeleteApp}>
                            <Trash2 />
                            Delete App
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isRegenerateSecretOpen} onOpenChange={setIsRegenerateSecretOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={RefreshCw}
                        tone="warning"
                        title="Regenerate Client Secret"
                        subtitle="Generate a new client secret for this app. The old secret will be invalidated immediately."
                    />
                    {regeneratingApp && (
                        <div className="vault-dialog-body">
                            {!newlyGeneratedSecret ? (
                                <div className="vault-callout" data-tone="warning">
                                    <AlertTriangle aria-hidden="true" />
                                    <div>
                                        <p className="vault-callout-title">Warning: This action is irreversible</p>
                                        <p>
                                            The current secret for <strong>{regeneratingApp.name}</strong> will be invalidated.
                                            All active tokens will continue to work until they expire.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="vault-callout" data-tone="success">
                                        <CheckCircle2 aria-hidden="true" />
                                        <p className="vault-callout-title">New secret generated successfully</p>
                                    </div>
                                    <VaultValueField label="New Client Secret" value={showNewlyGeneratedSecret ? newlyGeneratedSecret : MASKED_SECRET}>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="vault-icon-button no-default-hover-elevate"
                                            aria-label={showNewlyGeneratedSecret ? "Hide client secret" : "Show client secret"}
                                            onClick={() => setShowNewlyGeneratedSecret(!showNewlyGeneratedSecret)}
                                        >
                                            {showNewlyGeneratedSecret ? <EyeOff /> : <Eye />}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="vault-icon-button no-default-hover-elevate"
                                            aria-label="Copy client secret"
                                            onClick={() => copyToClipboard(newlyGeneratedSecret, "Client Secret")}
                                        >
                                            <Copy />
                                        </Button>
                                    </VaultValueField>
                                </>
                            )}
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        {!newlyGeneratedSecret ? (
                            <>
                                <Button variant="outline" className="vault-dialog-button" onClick={() => setIsRegenerateSecretOpen(false)}>Cancel</Button>
                                <Button className="vault-dialog-button" data-tone="warning" onClick={handleConfirmRegenerateSecret}>
                                    <RefreshCw />
                                    Regenerate Secret
                                </Button>
                            </>
                        ) : (
                            <Button className="vault-dialog-button" data-tone="primary" onClick={() => setIsRegenerateSecretOpen(false)}>Done</Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isViewSecretOpen} onOpenChange={setIsViewSecretOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={Lock}
                        tone="warning"
                        title="View Secret"
                        subtitle="Secret value stored in the vault."
                    />
                    {viewingSecret && (
                        <div className="vault-dialog-body">
                            <div className="vault-detail-grid">
                                <div className="vault-field">
                                    <span className="vault-field-label">Label</span>
                                    <p className="vault-field-text" data-mono="true">{viewingSecret.label}</p>
                                </div>
                                <div className="vault-field">
                                    <span className="vault-field-label">Vault Path</span>
                                    <p className="vault-field-text" data-mono="true">{viewingSecret.vaultPath}</p>
                                </div>
                            </div>
                            <div className="vault-field">
                                <div className="vault-field-head">
                                    <span className="vault-field-label">Secret Value (v{viewingSecret.version})</span>
                                    <Button variant="outline" size="sm" className="vault-text-button no-default-hover-elevate" onClick={() => setShowSecretValue(!showSecretValue)}>
                                        {showSecretValue ? <><EyeOff />Hide</> : <><Eye />Reveal</>}
                                    </Button>
                                </div>
                                <div className="vault-secret-box">
                                    <pre data-hidden={!showSecretValue}>{secretValue}</pre>
                                    {showSecretValue && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="vault-text-button vault-secret-copy no-default-hover-elevate"
                                            onClick={() => copyToClipboard(secretValue, "Secret Value")}
                                        >
                                            <Copy />
                                            Copy
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsViewSecretOpen(false)}>Close</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isUpdateSecretOpen} onOpenChange={setIsUpdateSecretOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={Edit}
                        title="Update Secret"
                        subtitle="Create a new version of this secret. The previous version will be retained in history."
                    />
                    {updatingSecret && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={Lock} tone="amber" name={updatingSecret.label} mono sub={`Current version: v${updatingSecret.version}`} />
                            <div className="vault-field">
                                <Label htmlFor="updateSecretValue" className="vault-field-label">New Secret Value (JSON)</Label>
                                <Textarea
                                    id="updateSecretValue"
                                    value={updateSecretValue}
                                    onChange={(e) => setUpdateSecretValue(e.target.value)}
                                    placeholder='{"username": "new_user", "password": "new_password"}'
                                    rows={6}
                                    className="font-mono text-sm"
                                />
                            </div>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsUpdateSecretOpen(false)}>Cancel</Button>
                        <Button className="vault-dialog-button" data-tone="primary" onClick={handleUpdateSecret}>Update Secret</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isVersionHistoryOpen} onOpenChange={setIsVersionHistoryOpen}>
                <DialogContent className="vault-dialog sm:max-w-lg">
                    <VaultDialogHeader
                        icon={History}
                        title="Version History"
                        subtitle="View all versions of this secret."
                    />
                    {versionHistorySecret && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={Lock} tone="amber" name={versionHistorySecret.label} mono />
                            <div className="vault-version-list">
                                {Array.from({ length: versionHistorySecret.version }, (_, i) => versionHistorySecret.version - i).map((v) => (
                                    <div key={v} className="vault-version" data-current={v === versionHistorySecret.version}>
                                        <div className="vault-version-tags">
                                            <span className="vault-tag" data-mono="true">v{v}</span>
                                            {v === versionHistorySecret.version && (
                                                <VaultPill tone="success">Current</VaultPill>
                                            )}
                                        </div>
                                        <span className="vault-version-date">
                                            {new Date(new Date(versionHistorySecret.updatedAt).getTime() - (versionHistorySecret.version - v) * 86400000 * 7).toLocaleDateString()}
                                        </span>
                                        {v !== versionHistorySecret.version && (
                                            <div className="vault-version-actions">
                                                <Button variant="outline" size="sm" className="vault-text-button no-default-hover-elevate">View</Button>
                                                <Button variant="outline" size="sm" className="vault-text-button no-default-hover-elevate">Rollback</Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsVersionHistoryOpen(false)}>Close</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeleteSecretOpen} onOpenChange={setIsDeleteSecretOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={AlertTriangle}
                        tone="danger"
                        title="Delete Secret"
                        subtitle="This action cannot be undone. This will permanently delete the secret and all its versions from the vault."
                    />
                    {deletingSecret && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={Lock} tone="danger" name={deletingSecret.label} mono sub={`Client: ${deletingSecret.clientName}`}>
                                <span className="vault-tag">{deletingSecret.version} version(s) will be deleted</span>
                            </VaultEntity>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsDeleteSecretOpen(false)}>Cancel</Button>
                        <Button variant="destructive" className="vault-dialog-button" onClick={handleDeleteSecret}>
                            <Trash2 />
                            Delete Secret
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isRevokeTokenOpen} onOpenChange={setIsRevokeTokenOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={Ban}
                        tone="danger"
                        title="Revoke Token"
                        subtitle="This action cannot be undone. The token will be immediately invalidated."
                    />
                    {revokingToken && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={KeyRound} tone="danger" name={revokingToken.appName} sub={`Client: ${revokingToken.clientName}`} />
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsRevokeTokenOpen(false)}>Cancel</Button>
                        <Button variant="destructive" className="vault-dialog-button" onClick={handleConfirmRevokeToken}>
                            <Ban />
                            Revoke Token
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeactivateClientOpen} onOpenChange={setIsDeactivateClientOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={deactivatingClient?.active ? XCircle : CheckCircle2}
                        tone={deactivatingClient?.active ? "warning" : "success"}
                        title={deactivatingClient?.active ? "Deactivate Client Group" : "Activate Client Group"}
                        subtitle={deactivatingClient?.active
                            ? "Deactivating this client group will have the following effects:"
                            : "Activating this client group will restore access for all its apps."}
                    />
                    {deactivatingClient && deactivatingClient.active && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={Building2} tone="amber" name={deactivatingClient.name} />
                            <ul className="vault-effects">
                                <li className="vault-effect">
                                    <XCircle aria-hidden="true" />
                                    <span>All <strong>{clientApps.filter(a => a.clientId === deactivatingClient.id).length}</strong> app(s) will be deactivated</span>
                                </li>
                                <li className="vault-effect">
                                    <Ban aria-hidden="true" />
                                    All active tokens will be immediately revoked
                                </li>
                                <li className="vault-effect">
                                    <Lock aria-hidden="true" />
                                    Apps will not be able to access secrets
                                </li>
                            </ul>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsDeactivateClientOpen(false)}>Cancel</Button>
                        <Button
                            className="vault-dialog-button"
                            data-tone={deactivatingClient?.active ? "warning" : "success"}
                            onClick={handleConfirmDeactivateClient}
                        >
                            {deactivatingClient?.active ? "Deactivate" : "Activate"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeactivateAppOpen} onOpenChange={setIsDeactivateAppOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={deactivatingApp?.active ? XCircle : CheckCircle2}
                        tone={deactivatingApp?.active ? "warning" : "success"}
                        title={deactivatingApp?.active ? "Deactivate App" : "Activate App"}
                        subtitle={deactivatingApp?.active
                            ? "Deactivating this app will have the following effects:"
                            : "Activating this app will restore its access to secrets."}
                    />
                    {deactivatingApp && deactivatingApp.active && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={AppWindow} tone="amber" name={deactivatingApp.name} mono sub={`Client: ${deactivatingApp.clientName}`} />
                            <ul className="vault-effects">
                                <li className="vault-effect">
                                    <Ban aria-hidden="true" />
                                    <span><strong>{deactivatingApp.activeTokens}</strong> active token(s) will be revoked</span>
                                </li>
                                <li className="vault-effect">
                                    <Lock aria-hidden="true" />
                                    App will not be able to authenticate or access secrets
                                </li>
                            </ul>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsDeactivateAppOpen(false)}>Cancel</Button>
                        <Button
                            className="vault-dialog-button"
                            data-tone={deactivatingApp?.active ? "warning" : "success"}
                            onClick={handleConfirmDeactivateApp}
                        >
                            {deactivatingApp?.active ? "Deactivate" : "Activate"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={isRevokeAllTokensOpen} onOpenChange={setIsRevokeAllTokensOpen}>
                <DialogContent className="vault-dialog sm:max-w-md">
                    <VaultDialogHeader
                        icon={Ban}
                        tone="danger"
                        title="Revoke All Tokens"
                        subtitle="This will immediately invalidate all active tokens for this app."
                    />
                    {revokeAllTokensApp && (
                        <div className="vault-dialog-body">
                            <VaultEntity icon={AppWindow} tone="danger" name={revokeAllTokensApp.name} mono sub={`Client: ${revokeAllTokensApp.clientName}`}>
                                <span className="vault-tag">{revokeAllTokensApp.activeTokens} active token(s) will be revoked</span>
                            </VaultEntity>
                            <p className="vault-field-hint">The app will need to re-authenticate to obtain new tokens.</p>
                        </div>
                    )}
                    <div className="vault-dialog-footer">
                        <Button variant="outline" className="vault-dialog-button" onClick={() => setIsRevokeAllTokensOpen(false)}>Cancel</Button>
                        <Button variant="destructive" className="vault-dialog-button" onClick={handleConfirmRevokeAllTokens}>
                            <Ban />
                            Revoke All Tokens
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
